<script setup lang="ts">
defineOptions({ customElement: true })
import { computed, getCurrentInstance, onMounted, ref, onBeforeUnmount, nextTick, watch } from 'vue'
import {
  OpenVidu,
  Publisher,
  type PublisherProperties,
  Session,
  StreamEvent,
  Subscriber,
} from 'openvidu-browser'
import UserVideo from '@/components/UserVideo.vue'
import MicOff from '@/components/icons/MicOff.vue'
import MicOn from '@/components/icons/MicOn.vue'
import CamOff from '@/components/icons/CamOff.vue'
import CamOn from '@/components/icons/CamOn.vue'
import ExitDoor from '@/components/icons/ExitDoor.vue'

const props = defineProps({
  sessionToken: {
    type: String,
    required: true,
  },
  screenToken: {
    type: String,
    default: '',
  },
  lang: {
    type: String,
    default: '',
  },
  appTitle: {
    type: String,
    default: 'Telemedicis',
  },
  onlineUsers: {
    type: [Array, String],
    default: () => [],
  },
  inCallUsers: {
    type: [Array, String],
    default: () => [],
  },
  inPreviewUsers: {
    type: [Array, String],
    default: () => [],
  },
  colleagues: {
    type: [Array, String],
    default: () => [],
  },
  authToken: {
    type: String,
    default: '',
  },
  apiUrl: {
    type: String,
    default: '',
  },
})

const session = ref<Session | undefined>(undefined)
const mainStreamManager = ref<Publisher | Subscriber | undefined>(undefined)
const publisher = ref<Publisher | undefined>(undefined)
const subscribers = ref<Subscriber[]>([])
const OV = new OpenVidu()
const hasJoinedSession = ref(false)

const participants = ref<any[]>([])
let isConnectedPromiseResolved = false

function addParticipant(connection: any) {
  if (!connection) return
  if (!participants.value.some((p) => p.connectionId === connection.connectionId)) {
    participants.value.push(connection)
  }
}

function removeParticipant(connection: any) {
  if (!connection) return
  const idx = participants.value.findIndex((p) => p.connectionId === connection.connectionId)
  if (idx !== -1) participants.value.splice(idx, 1)
}

function parseConnectionData(data: string | undefined) {
  if (!data) return { userName: '', userId: '' }
  try {
    const parsed = JSON.parse(data)
    return { userName: parsed.userName || '', userId: parsed.userId || '' }
  } catch {
    return { userName: data, userId: '' }
  }
}

const isConnectingScreen = ref(false)
const screenOV = ref<OpenVidu | null>(null)
const screenSession = ref<Session | null>(null)

const localUserId = computed(() => {
  // Prefer session.connection so device-less users (no publisher) also get an ID
  const conn = session.value?.connection
  if (conn && conn.data) {
    try { return JSON.parse(conn.data).userId } catch { /* ignore */ }
  }
  if (publisher.value?.stream?.connection) {
    try { return JSON.parse(publisher.value.stream.connection.data).userId } catch { /* ignore */ }
  }
  return null
})

const isHost = computed(() => {
  return !!localUserId.value && !localUserId.value.startsWith('guest-')
})


function getUserIdOfConnection(connection: any): string | null {
  if (connection?.data) {
    try { return JSON.parse(connection.data).userId } catch { return connection.data }
  }
  return null
}

function getUserNameOfConnection(connection: any): string {
  if (connection?.data) {
    const { userName, userId } = parseConnectionData(connection.data)
    return userName || userId || ''
  }
  return ''
}


function canManageConnection(connection: any): boolean {
  if (!isHost.value) return false
  const targetId = getUserIdOfConnection(connection)
  if (!targetId || !localUserId.value) return false
  return targetId !== localUserId.value && targetId !== localUserId.value + '-screen'
}

const cameras = ref<MediaDeviceInfo[]>([])
const microphones = ref<MediaDeviceInfo[]>([])
const speakers = ref<MediaDeviceInfo[]>([])
const isWaitingForPermissions = ref(false)

const selectedCameraId = ref<MediaStreamTrack | string | boolean>(false)
const selectedMicId = ref<MediaStreamTrack | string | boolean>(false)
const selectedSpeakerId = ref<string>(localStorage.getItem('selected-speaker-id') || '')

const previewContainer = ref<HTMLElement | undefined>(undefined)
let previewPublisher: Publisher | null = null

// Mic analysis
let audioContext: AudioContext | null = null
let analyser: AnalyserNode | null = null
let micStream: MediaStream | null = null
const micVolume = ref(0)
// Removed old setInterval-based meter; using requestAnimationFrame instead
let rafId: number | null = null

// Mute speaking detection
const showMuteWarning = ref(false)
let muteWarningTimeout: any = null
let muteDetectorRafId: number | null = null
let muteDetectorAudioCtx: AudioContext | null = null
let muteDetectorStream: MediaStream | null = null
let muteDetectorAnalyser: AnalyserNode | null = null
let muteConsecutiveSpeakingFrames = 0
let lastMuteWarningTime = 0

const isMicMuted = ref(false)
const isVideoOff = ref(false)

const isScreenSharing = ref(false)
const screenPublisher = ref<Publisher | undefined>(undefined)

const pinnedStreamIds = ref<string[]>([])

const hasCameraAccess = computed(() => {
  return cameras.value.length > 0 && cameras.value.some((cam) => cam.deviceId)
})

const hasMicAccess = computed(() => {
  return microphones.value.length > 0 && microphones.value.some((mic) => mic.deviceId)
})

const remoteCameraStreamIds = ref<string[]>([])

const humanParticipantCount = computed(() => {
  // Count real connections (excludes screen-share connections)
  return participants.value.filter((conn) => {
    const { userId } = parseConnectionData(conn.data)
    return !userId.endsWith('-screen')
  }).length
})

const meetingDurationSeconds = ref(0)
const hasMeetingStarted = ref(false)
const meetingStartTimestamp = ref<number | null>(null)
const aloneDurationSeconds = ref(0)
let timerInterval: number | null = null

function startMeetingTimer() {
  if (hasMeetingStarted.value && meetingStartTimestamp.value) return
  hasMeetingStarted.value = true
  meetingStartTimestamp.value = Date.now()
  if (session.value) {
    session.value.signal({
      type: 'timer-sync',
      data: meetingStartTimestamp.value.toString()
    }).catch((err) => console.error('Error sending timer-sync signal:', err))
  }
}

function playNotificationSound() {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
    
    // Play first beep
    const osc1 = audioCtx.createOscillator()
    const gain1 = audioCtx.createGain()
    osc1.connect(gain1)
    gain1.connect(audioCtx.destination)
    
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(880, audioCtx.currentTime)
    gain1.gain.setValueAtTime(0.3, audioCtx.currentTime)
    gain1.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.15)
    
    osc1.start(audioCtx.currentTime)
    osc1.stop(audioCtx.currentTime + 0.15)
    
    // Play second beep after 200ms
    setTimeout(() => {
      try {
        const osc2 = audioCtx.createOscillator()
        const gain2 = audioCtx.createGain()
        osc2.connect(gain2)
        gain2.connect(audioCtx.destination)
        
        osc2.type = 'sine'
        osc2.frequency.setValueAtTime(880, audioCtx.currentTime)
        gain2.gain.setValueAtTime(0.3, audioCtx.currentTime)
        gain2.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.15)
        
        osc2.start(audioCtx.currentTime)
        osc2.stop(audioCtx.currentTime + 0.15)
      } catch {
        // ignore
      }
    }, 200)
  } catch (err) {
    console.error('Failed to play Web Audio alert beep:', err)
  }
}

function playMuteWarningSound() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()

    const playBeeps = () => {
      try {
        const now = ctx.currentTime

        // Tone 1: 660Hz
        const osc1 = ctx.createOscillator()
        const gain1 = ctx.createGain()
        osc1.type = 'sine'
        osc1.frequency.setValueAtTime(660, now)
        gain1.gain.setValueAtTime(0.001, now)
        gain1.gain.linearRampToValueAtTime(0.35, now + 0.015)
        gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.14)
        osc1.connect(gain1)
        gain1.connect(ctx.destination)
        osc1.start(now)
        osc1.stop(now + 0.14)

        // Tone 2: 880Hz
        const osc2 = ctx.createOscillator()
        const gain2 = ctx.createGain()
        osc2.type = 'sine'
        osc2.frequency.setValueAtTime(880, now + 0.10)
        gain2.gain.setValueAtTime(0.001, now + 0.10)
        gain2.gain.linearRampToValueAtTime(0.40, now + 0.115)
        gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.26)
        osc2.connect(gain2)
        gain2.connect(ctx.destination)
        osc2.start(now + 0.10)
        osc2.stop(now + 0.26)
      } catch (e) {
        console.warn('Error rendering mute warning oscillators:', e)
      } finally {
        setTimeout(() => {
          ctx.close().catch(() => {})
        }, 500)
      }
    }

    const runWithResume = () => {
      if (ctx.state === 'suspended') {
        ctx.resume().then(playBeeps).catch(playBeeps)
      } else {
        playBeeps()
      }
    }

    if (selectedSpeakerId.value && typeof (ctx as any).setSinkId === 'function') {
      ;(ctx as any).setSinkId(selectedSpeakerId.value).then(runWithResume).catch(runWithResume)
    } else {
      runWithResume()
    }
  } catch (err) {
    console.warn('Failed to play mute warning sound:', err)
  }
}

watch(humanParticipantCount, (newCount) => {
  if (newCount >= 2 && !hasMeetingStarted.value) {
    startMeetingTimer()
  }
})

const formattedMeetingTime = computed(() => {
  if (!hasMeetingStarted.value) {
    return '--:--'
  }
  const totalSeconds = meetingDurationSeconds.value
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  
  const paddedMinutes = String(minutes).padStart(2, '0')
  const paddedSeconds = String(seconds).padStart(2, '0')
  
  if (hours > 0) {
    const paddedHours = String(hours).padStart(2, '0')
    return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
  }
  return `${paddedMinutes}:${paddedSeconds}`
})

const allStreamManagers = computed(() => {
  const list: any[] = []
  if (publisher.value) list.push(publisher.value)
  if (isScreenSharing.value && screenPublisher.value) list.push(screenPublisher.value)
  list.push(...subscribers.value)
  return list
})

// Keep allStreams as alias for backward-compat (screen-share watcher uses it)
const allStreams = allStreamManagers

interface ParticipantItem {
  id: string
  connection: any
  streamManager?: any
  isScreen?: boolean
}

const participantItems = computed<ParticipantItem[]>(() => {
  const items: ParticipantItem[] = []
  const localConn = session.value?.connection

  const sorted = [...participants.value].sort((a, b) => {
    const isLocalA = localConn && a.connectionId === localConn.connectionId
    const isLocalB = localConn && b.connectionId === localConn.connectionId
    if (isLocalA) return -1
    if (isLocalB) return 1
    return a.connectionId.localeCompare(b.connectionId)
  })

  for (const conn of sorted) {
    const { userId } = parseConnectionData(conn.data)
    if (userId.endsWith('-screen')) continue // screen connections have their own stream item

    const cameraStream = allStreamManagers.value.find(
      (sm) => sm.stream?.connection?.connectionId === conn.connectionId && sm.stream?.typeOfVideo !== 'SCREEN'
    )
    items.push({ id: conn.connectionId, connection: conn, streamManager: cameraStream })

    const screenStream = allStreamManagers.value.find((sm) => {
      if (!sm.stream?.connection) return false
      const smUserId = parseConnectionData(sm.stream.connection.data).userId
      return smUserId === `${userId}-screen`
    })
    if (screenStream) {
      items.push({
        id: screenStream.stream?.streamId || `${conn.connectionId}-screen`,
        connection: screenStream.stream?.connection || conn,
        streamManager: screenStream,
        isScreen: true
      })
    }
  }
  return items
})

const gridItems = computed(() => {
  const all = participantItems.value
  if (pinnedStreamIds.value.length === 0) return all
  return all.filter((item) => pinnedStreamIds.value.includes(item.id))
})

const sidebarItems = computed(() => {
  if (pinnedStreamIds.value.length === 0) return []
  return participantItems.value.filter((item) => !pinnedStreamIds.value.includes(item.id))
})

// Keep legacy aliases used in template (will update template too)
const gridStreams = gridItems
const sidebarStreams = sidebarItems

const gridLayout = computed(() => {
  const n = gridStreams.value.length
  let cols = 1
  let rows = 1

  if (n <= 1) {
    cols = 1
    rows = 1
  } else if (n === 2) {
    cols = 2
    rows = 1
  } else if (n === 3) {
    cols = 3
    rows = 1
  } else if (n === 4) {
    cols = 2
    rows = 2
  } else if (n <= 6) {
    cols = 3
    rows = 2
  } else if (n <= 8) {
    cols = 4
    rows = 2
  } else if (n === 9) {
    cols = 3
    rows = 3
  } else if (n <= 12) {
    cols = 4
    rows = 3
  } else if (n <= 16) {
    cols = 4
    rows = 4
  } else {
    cols = Math.ceil(Math.sqrt(n))
    rows = Math.ceil(n / cols)
  }

  return { cols, rows }
})


function togglePin(streamId: string | undefined) {
  if (!streamId) return
  const idx = pinnedStreamIds.value.indexOf(streamId)
  if (idx !== -1) {
    pinnedStreamIds.value.splice(idx, 1)
  } else {
    pinnedStreamIds.value.push(streamId)
  }
}

watch(allStreams, (newStreams) => {
  // Auto-pin any screen share streams that are not already pinned
  const screenStreamIds = newStreams
    .map(sm => sm?.stream)
    .filter(stream => stream && stream.typeOfVideo === 'SCREEN')
    .map(stream => stream.streamId) as string[]

  for (const streamId of screenStreamIds) {
    if (!pinnedStreamIds.value.includes(streamId)) {
      pinnedStreamIds.value.push(streamId)
    }
  }

  // Clean up any pinnedStreamIds that are no longer in allStreams
  const activeStreamIds = new Set(newStreams.map(sm => sm?.stream?.streamId).filter(Boolean))
  pinnedStreamIds.value = pinnedStreamIds.value.filter(id => activeStreamIds.has(id))
}, { immediate: true })

watch(() => props.screenToken, async (newToken) => {
  if (newToken && isConnectingScreen.value) {
    await connectScreenShare(newToken)
  }
})

async function connectScreenShare(token: string) {
  try {
    const sOV = screenOV.value
    const pub = screenPublisher.value
    if (!sOV || !pub) {
      console.warn('screenOV or screenPublisher not initialized')
      stopScreenShare()
      return
    }

    const sSession = sOV.initSession()
    screenSession.value = sSession

    await sSession.connect(token)
    await sSession.publish(pub as any)

    isScreenSharing.value = true
    isConnectingScreen.value = false
    mainStreamManager.value = pub
    dispatchHostEvent('screen-share-started')
  } catch (err) {
    console.warn('Screen share connection/publish failed:', err)
    stopScreenShare()
  }
}

async function startScreenShare() {
  if (isScreenSharing.value || isConnectingScreen.value) return
  isConnectingScreen.value = true

  try {
    const sOV = new OpenVidu()
    screenOV.value = sOV

    const screenPub = sOV.initPublisher(undefined, {
      videoSource: 'screen',
      audioSource: false,
      publishAudio: false,
      publishVideo: true,
      mirror: false,
      resolution: '1920x1080',
      frameRate: 15,
    })

    screenPublisher.value = screenPub

    screenPub.once('accessAllowed', () => {
      const stream = screenPub.stream
      if (stream && stream.getMediaStream()) {
        const videoTrack = stream.getMediaStream().getVideoTracks()[0]
        if (videoTrack) {
          videoTrack.addEventListener('ended', () => {
            stopScreenShare()
          })
        }
      }
      dispatchHostEvent('screen-token-needed')
    })

    screenPub.once('accessDenied', () => {
      console.warn('Screen share access denied')
      stopScreenShare()
    })
  } catch (err) {
    console.warn('Failed to initialize screen share publisher:', err)
    stopScreenShare()
  }
}

async function stopScreenShare() {
  isScreenSharing.value = false
  isConnectingScreen.value = false
  const pub = screenPublisher.value
  screenPublisher.value = undefined

  if (pub) {
    try {
      const stream = pub.stream
      if (stream && stream.getMediaStream()) {
        stream.getMediaStream().getTracks().forEach(t => t.stop())
      }
    } catch (e) {
      console.warn('Error stopping screen tracks:', e)
    }

    if (screenSession.value) {
      try {
        await screenSession.value.unpublish(pub as any)
      } catch (err) {
        console.warn('Failed to unpublish screen share:', err)
      }
    }
    dispatchHostEvent('screen-share-stopped')
  }

  if (screenSession.value) {
    try {
      screenSession.value.disconnect()
    } catch (err) {
      console.warn('Failed to disconnect screen session:', err)
    }
    screenSession.value = null
  }
  screenOV.value = null

  if (mainStreamManager.value === pub || !mainStreamManager.value) {
    mainStreamManager.value = publisher.value
  }
}

function toggleScreenShare() {
  if (isScreenSharing.value || isConnectingScreen.value) {
    stopScreenShare()
  } else {
    startScreenShare()
  }
}

const selectedStreamId = ref<string | null>(null)
const subscriberMutedMap = ref<Record<string, boolean>>({})
const subscriberVideoOffMap = ref<Record<string, boolean>>({})
const speakingMap = ref<Record<string, boolean>>({})

const showCameraDropdown = ref(false)
const showMicDropdown = ref(false)
const showSpeakerDropdown = ref(false)
let lastCameraSwitchStream: MediaStream | null = null
let lastMicSwitchStream: MediaStream | null = null

const micToggleLabel = computed(() => (isMicMuted.value ? 'Unmute mic' : 'Mute mic'))
const videoToggleLabel = computed(() =>
  isVideoOff.value ? 'Turn video on' : 'Turn video off',
)
const isConnecting = ref(false)
const awaitingTokenRefresh = ref(false)
let hasRequestedLeave = false
const lastTokenUsed = ref<string | null>(null)
const retryAttempts = ref(0)
const MAX_RETRY_ATTEMPTS = 10
let retryTimer: number | null = null
const RETRY_DELAY_MS = 2000

const isReconnecting = computed(() => awaitingTokenRefresh.value || isConnecting.value)
const expiredToken = ref<string | null>(null)
const connectionOverlayLabel = computed(() => {
  if (expiredToken.value) {
    return 'Your video session has expired. Please initiate the call again.'
  }
  return awaitingTokenRefresh.value ? 'Reconnecting…' : 'Connecting…'
})
const isConnectionOverlayVisible = computed(
  () => isReconnecting.value || Boolean(expiredToken.value),
)

const hostInstance = getCurrentInstance()
const dispatchHostEvent = (name: string, detail?: unknown) => {
  const hostEl = hostInstance?.proxy?.$el as HTMLElement | undefined
  if (!hostEl) return
  hostEl.dispatchEvent(
    new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true,
    }),
  )
}

interface ChatAttachment {
  name: string
  size: number
  type: string
  url: string
}

interface ChatMessage {
  text: string
  senderName: string
  senderId: string
  timestamp: number
  replyTo?: {
    senderName: string
    text: string
  }
  attachment?: ChatAttachment
}

const isChatOpen = ref(false)
const isExternalChatOpen = ref(false)
const unreadChatCount = ref(0)
const chatInputText = ref('')
const chatMessages = ref<Array<ChatMessage>>([])
const chatFeedContainer = ref<HTMLElement | null>(null)
const replyingTo = ref<{ senderName: string; text: string } | null>(null)

if (typeof window !== 'undefined') {
  window.addEventListener('global-chat-visibility-change', (e: any) => {
    isExternalChatOpen.value = Boolean(e.detail?.open)
    unreadChatCount.value = 0
  })
}

const fileInputRef = ref<HTMLInputElement | null>(null)
const pendingAttachment = ref<ChatAttachment | null>(null)
const attachmentError = ref<string | null>(null)

function triggerFilePicker() {
  attachmentError.value = null
  if (fileInputRef.value) {
    fileInputRef.value.click()
  }
}

function compressImage(file: File, maxWidth = 1200, maxHeight = 1200, quality = 0.85): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        let width = img.width
        let height = img.height

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width)
            width = maxWidth
          } else {
            width = Math.round((width * maxHeight) / height)
            height = maxHeight
          }
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height)
          resolve(canvas.toDataURL('image/jpeg', quality))
        } else {
          resolve(e.target?.result as string)
        }
      }
      img.onerror = () => resolve(e.target?.result as string)
      img.src = e.target?.result as string
    }
    reader.readAsDataURL(file)
  })
}

function openAttachment(url: string, name: string) {
  if (!url) return
  if (url.startsWith('data:')) {
    try {
      const parts = url.split(';base64,')
      if (parts.length < 2 || !parts[0] || !parts[1]) return
      const contentType = parts[0].replace('data:', '') || 'application/pdf'
      const raw = window.atob(parts[1])
      const rawLength = raw.length
      const uInt8Array = new Uint8Array(rawLength)
      for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i)
      }
      const blob = new Blob([uInt8Array], { type: contentType })
      const blobUrl = URL.createObjectURL(blob)
      window.open(blobUrl, '_blank')
    } catch {
      const win = window.open('', '_blank')
      if (win) {
        win.document.write(`<iframe src="${url}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`)
        win.document.title = name || 'Document'
      }
    }
  } else {
    window.open(url, '_blank')
  }
}

const isDraggingOver = ref(false)

async function processFileForAttachment(file: File) {
  if (file.type.startsWith('image/')) {
    if (file.size > 5 * 1024 * 1024) {
      attachmentError.value = 'Imaginea depășește limita de 5 MB.'
      return
    }
    const compressedUrl = await compressImage(file)
    const fileName = file.name && file.name !== 'image.png' ? file.name : `screenshot_${Date.now()}.png`
    pendingAttachment.value = {
      name: fileName,
      size: file.size,
      type: file.type || 'image/png',
      url: compressedUrl
    }
    attachmentError.value = null
  } else {
    if (file.size > 1.5 * 1024 * 1024) {
      attachmentError.value = 'Fișierul este prea mare. Dimensiunea maximă permisă pentru documente este de 1.5 MB.'
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      pendingAttachment.value = {
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
        url: e.target?.result as string
      }
      attachmentError.value = null
    }
    reader.readAsDataURL(file)
  }
}

async function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return
  await processFileForAttachment(file)
  target.value = ''
}

async function handlePaste(event: ClipboardEvent) {
  const items = event.clipboardData?.items
  if (!items) return

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (!item) continue
    if (item.type.indexOf('image') !== -1) {
      event.preventDefault()
      const file = item.getAsFile()
      if (!file) continue
      await processFileForAttachment(file)
      break
    }
  }
}

function handleDragOver(e: DragEvent) {
  e.preventDefault()
  if (e.dataTransfer?.types?.includes('Files')) {
    isDraggingOver.value = true
  }
}

function handleDragLeave(e: DragEvent) {
  e.preventDefault()
  const rect = (e.currentTarget as HTMLElement)?.getBoundingClientRect?.()
  if (rect) {
    if (e.clientX <= rect.left || e.clientX >= rect.right || e.clientY <= rect.top || e.clientY >= rect.bottom) {
      isDraggingOver.value = false
    }
  } else {
    isDraggingOver.value = false
  }
}

function handleDrop(e: DragEvent) {
  e.preventDefault()
  isDraggingOver.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) {
    processFileForAttachment(file)
  }
}

function removePendingAttachment() {
  pendingAttachment.value = null
  attachmentError.value = null
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const activeImagePreviewUrl = ref<string | null>(null)
const activeImagePreviewName = ref<string | null>(null)

function openImagePreview(attachment: ChatAttachment) {
  activeImagePreviewUrl.value = attachment.url
  activeImagePreviewName.value = attachment.name
}

function closeImagePreview() {
  activeImagePreviewUrl.value = null
  activeImagePreviewName.value = null
}

function setReplyToMessage(msg: ChatMessage) {
  let displaySnippet = msg.text || ''
  if (msg.attachment) {
    const attachLabel = `[Attachment: ${msg.attachment.name}]`
    displaySnippet = displaySnippet ? `${attachLabel} ${displaySnippet}` : attachLabel
  }
  replyingTo.value = {
    senderName: msg.senderName,
    text: displaySnippet.length > 80 ? displaySnippet.slice(0, 80) + '...' : displaySnippet
  }
  nextTick(() => {
    if (chatInputRef.value) {
      chatInputRef.value.focus()
    }
  })
}

function cancelReply() {
  replyingTo.value = null
}

function toggleChatPanel() {
  const ev = new CustomEvent('toggle-global-chat', { cancelable: true, bubbles: true, composed: true })
  if (typeof window !== 'undefined') {
    window.dispatchEvent(ev)
  }

  if (ev.defaultPrevented) {
    unreadChatCount.value = 0
    return
  }

  isChatOpen.value = !isChatOpen.value
  if (isChatOpen.value) {
    unreadChatCount.value = 0
    scrollToBottomChat()
  }
}

let sharedChatAudioCtx: AudioContext | null = null

function initAudioCtxOnInteraction() {
  if (!sharedChatAudioCtx) {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    if (AudioCtx) {
      sharedChatAudioCtx = new AudioCtx()
    }
  }
  if (sharedChatAudioCtx && sharedChatAudioCtx.state === 'suspended') {
    sharedChatAudioCtx.resume().catch(() => {})
  }
}

if (typeof window !== 'undefined') {
  const events = ['click', 'keydown', 'touchstart']
  const warmUpListener = () => {
    initAudioCtxOnInteraction()
    events.forEach(e => window.removeEventListener(e, warmUpListener))
  }
  events.forEach(e => window.addEventListener(e, warmUpListener, { passive: true }))
}

function playChatNotificationSound() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioCtx) return
    if (!sharedChatAudioCtx || sharedChatAudioCtx.state === 'closed') {
      sharedChatAudioCtx = new AudioCtx()
    }
    const ctx = sharedChatAudioCtx

    const playSound = () => {
      const startTime = ctx.currentTime + 0.03
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()

      osc.type = 'sine'
      osc.frequency.setValueAtTime(880, startTime)
      osc.frequency.exponentialRampToValueAtTime(1320, startTime + 0.08)

      gain.gain.cancelScheduledValues(startTime)
      gain.gain.setValueAtTime(0.001, startTime)
      gain.gain.linearRampToValueAtTime(0.45, startTime + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.22)

      osc.connect(gain)
      gain.connect(ctx.destination)

      osc.start(startTime)
      osc.stop(startTime + 0.22)
    }

    if (ctx.state === 'suspended') {
      ctx.resume().then(() => playSound()).catch(() => {})
    } else {
      playSound()
    }
  } catch (e) {
    // Ignore audio restrictions
  }
}

function scrollToBottomChat() {
  nextTick(() => {
    if (chatFeedContainer.value) {
      chatFeedContainer.value.scrollTop = chatFeedContainer.value.scrollHeight
    }
  })
}

function formatChatTime(timestamp: number): string {
  const d = new Date(timestamp)
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function isOwnMessage(msg: { senderId: string }): boolean {
  if (!msg.senderId) return false
  return msg.senderId === localUserId.value
}

function getInitials(name: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  const first = parts[0] || '?'
  if (parts.length === 1) return first.slice(0, 2).toUpperCase()
  const last = parts[parts.length - 1] || '?'
  const char1 = first[0] || '?'
  const char2 = last[0] || '?'
  return (char1 + char2).toUpperCase()
}

const AVATAR_COLORS: string[] = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#6366f1', '#06b6d4', '#14b8a6']
function getAvatarColor(name: string): string {
  const defaultColor = '#3b82f6'
  if (!name) return defaultColor
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length
  return AVATAR_COLORS[index] || defaultColor
}

function escapeHtml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function formatChatMessageHtml(text: string): string {
  if (!text) return ''
  let safe = escapeHtml(text)

  // Format **bold**
  safe = safe.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
  // Format *italic*
  safe = safe.replace(/\*(.*?)\*/g, '<em>$1</em>')

  // Format hyperlinks
  const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g
  safe = safe.replace(urlRegex, (url) => {
    const href = url.startsWith('www.') ? `http://${url}` : url
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="chat-link">${url}</a>`
  })

  // Line breaks
  safe = safe.replace(/\n/g, '<br>')
  return safe
}

const copiedMessageIndex = ref<number | null>(null)
function copyMessageText(text: string, index: number) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).then(() => {
      copiedMessageIndex.value = index
      setTimeout(() => {
        if (copiedMessageIndex.value === index) {
          copiedMessageIndex.value = null
        }
      }, 1500)
    }).catch(err => console.error('Failed to copy text', err))
  }
}

const isInviteLinkCopied = ref(false)
function copyInviteLink() {
  const link = window.location.href
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(link).then(() => {
      isInviteLinkCopied.value = true
      setTimeout(() => {
        isInviteLinkCopied.value = false
      }, 2500)
    }).catch((err) => console.error('Failed to copy invite link', err))
  } else {
    try {
      const textarea = document.createElement('textarea')
      textarea.value = link
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      isInviteLinkCopied.value = true
      setTimeout(() => {
        isInviteLinkCopied.value = false
      }, 2500)
    } catch (e) {
      console.error('Copy fallback failed', e)
    }
  }
}

function parseArrayProp(val: any): any[] {
  if (Array.isArray(val)) return val
  if (typeof val === 'string') {
    try {
      const res = JSON.parse(val)
      if (Array.isArray(res)) return res
    } catch {}
  }
  return []
}

const parsedOnlineUsers = computed<string[]>(() => parseArrayProp(props.onlineUsers))
const parsedInCallUsers = computed<string[]>(() => parseArrayProp(props.inCallUsers))
const parsedInPreviewUsers = computed<string[]>(() => parseArrayProp(props.inPreviewUsers))
const parsedColleagues = computed<any[]>(() => parseArrayProp(props.colleagues))

const showInviteModal = ref(false)
const colleagueSearchQuery = ref('')
const fetchedColleagues = ref<any[]>([])
const loadingColleagues = ref(false)
const errorColleagues = ref(false)
const invitedUserMap = ref<Record<string, boolean>>({})

function toggleInviteModal() {
  showInviteModal.value = !showInviteModal.value
  if (showInviteModal.value) {
    fetchColleaguesIfNeeded()
  }
}

async function fetchColleaguesIfNeeded() {
  if (parsedColleagues.value.length > 0) return
  if (fetchedColleagues.value.length > 0) return
  loadingColleagues.value = true
  errorColleagues.value = false
  try {
    const token = props.authToken || localStorage.getItem('user-token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }
    if (token) {
      headers['X-Auth-Token'] = token
      headers['Authorization'] = `Bearer ${token}`
    }

    let base = (props.apiUrl || '').replace(/\/+$/, '')
    let endpoint = '/users/staff-colleagues'
    if (!base) {
      base = '/api'
    }
    if (!base.endsWith('/api') && !base.endsWith('/api/v1')) {
      endpoint = '/api/users/staff-colleagues'
    }
    const url = `${base}${endpoint}`

    const res = await fetch(url, { headers })
    const contentType = res.headers.get('content-type') || ''
    if (res.ok && contentType.includes('application/json')) {
      const data = await res.json()
      fetchedColleagues.value = Array.isArray(data) ? data : (data.data ?? [])
    } else {
      console.warn(`Colleagues API request returned non-OK or non-JSON response (status: ${res.status}, contentType: ${contentType}, url: ${url})`)
      errorColleagues.value = true
    }
  } catch (err) {
    console.error('Failed to load colleagues for invite modal', err)
    errorColleagues.value = true
  } finally {
    loadingColleagues.value = false
  }
}

const allColleagues = computed(() => {
  if (parsedColleagues.value.length > 0) return parsedColleagues.value
  return fetchedColleagues.value
})

const filteredColleagues = computed(() => {
  const q = colleagueSearchQuery.value.trim().toLowerCase()
  let list = allColleagues.value
  if (q) {
    list = list.filter((c: any) => {
      const nameMatch = c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
      const roleMatch = Array.isArray(c.roles) && c.roles.some((r: string) => r.toLowerCase().includes(q))
      return nameMatch || roleMatch
    })
  }
  return [...list].sort((a: any, b: any) => {
    const aOnline = parsedOnlineUsers.value.includes(a.id) ? 0 : 1
    const bOnline = parsedOnlineUsers.value.includes(b.id) ? 0 : 1
    if (aOnline !== bOnline) return aOnline - bOnline
    return (a.name || '').localeCompare(b.name || '')
  })
})

function formatRoleName(roles: string[] | undefined): string {
  if (!roles || !Array.isArray(roles)) return ''
  return roles
    .filter((r) => r !== 'ROLE_USER')
    .map((r) => {
      switch (r) {
        case 'ROLE_DOCTOR': return 'Doctor'
        case 'ROLE_NURSE': return 'Asistent'
        case 'ROLE_SUPERVISOR': return 'Supervisor'
        case 'ROLE_ADMIN': return 'Admin'
        default: return r.replace('ROLE_', '').toLowerCase()
      }
    })
    .join(', ')
}

function inviteColleague(colleagueId: string) {
  invitedUserMap.value = { ...invitedUserMap.value, [colleagueId]: true }
  dispatchHostEvent('invite-user', { userId: colleagueId })
  setTimeout(() => {
    invitedUserMap.value = { ...invitedUserMap.value, [colleagueId]: false }
  }, 3000)
}

const chatInputRef = ref<HTMLInputElement | null>(null)

function insertFormatting(type: 'bold' | 'italic' | 'link') {
  const input = chatInputRef.value
  if (!input) return

  const start = input.selectionStart ?? chatInputText.value.length
  const end = input.selectionEnd ?? chatInputText.value.length
  const selected = chatInputText.value.substring(start, end)

  let prefix = ''
  let suffix = ''

  if (type === 'bold') {
    prefix = '**'
    suffix = '**'
  } else if (type === 'italic') {
    prefix = '*'
    suffix = '*'
  } else if (type === 'link') {
    prefix = 'https://'
    suffix = ''
  }

  const defaultInsert = type === 'link' ? 'example.com' : 'text'
  const replacement = selected ? `${prefix}${selected}${suffix}` : `${prefix}${defaultInsert}${suffix}`

  chatInputText.value =
    chatInputText.value.substring(0, start) +
    replacement +
    chatInputText.value.substring(end)

  nextTick(() => {
    input.focus()
    const selectStartPos = start + prefix.length
    const selectEndPos = selectStartPos + (selected ? selected.length : defaultInsert.length)
    input.setSelectionRange(selectStartPos, selectEndPos)
  })
}

function exportChatTranscript() {
  if (chatMessages.value.length === 0) return

  let content = `# Chat Transcript - ${props.appTitle || 'Telemedicis'}\n`
  content += `Date: ${new Date().toLocaleString()}\n`
  content += `----------------------------------------\n\n`

  const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g

  chatMessages.value.forEach(msg => {
    const timeStr = formatChatTime(msg.timestamp)
    let replySnippet = ''
    if (msg.replyTo) {
      const replyText = (msg.replyTo.text || '').replace(urlRegex, (url) => {
        const href = url.startsWith('www.') ? `https://${url}` : url
        return `[${url}](${href})`
      })
      replySnippet = `> Răspuns către ${msg.replyTo.senderName}: "${replyText}"\n`
    }
    let attachSnippet = ''
    if (msg.attachment) {
      attachSnippet = `[Attachment: ${msg.attachment.name} (${formatFileSize(msg.attachment.size)})]\n`
    }
    const msgText = (msg.text || '').replace(urlRegex, (url) => {
      const href = url.startsWith('www.') ? `https://${url}` : url
      return `[${url}](${href})`
    })
    content += `[${timeStr}] ${msg.senderName}:\n${replySnippet}${attachSnippet}${msgText}\n\n`
  })

  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `Chat_Transcript_${new Date().toISOString().slice(0, 10)}.md`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function sendChatMessage() {
  const text = chatInputText.value.trim()
  const attachment = pendingAttachment.value ? { ...pendingAttachment.value } : undefined

  if (!text && !attachment) return

  const senderName = (session.value?.connection?.data ? parseConnectionData(session.value.connection.data).userName : '') || 'Participant'
  const senderId = localUserId.value || ''
  const timestamp = Date.now()
  const replyTo = replyingTo.value ? { ...replyingTo.value } : undefined

  const msgObj: ChatMessage = { text, senderName, senderId, timestamp, replyTo, attachment }

  chatMessages.value.push(msgObj)
  chatInputText.value = ''
  replyingTo.value = null
  pendingAttachment.value = null
  attachmentError.value = null
  scrollToBottomChat()

  if (session.value) {
    const jsonStr = JSON.stringify(msgObj)
    const CHUNK_SIZE = 16384
    const totalChunks = Math.ceil(jsonStr.length / CHUNK_SIZE)

    if (totalChunks === 1) {
      session.value.signal({
        data: jsonStr,
        type: 'chat'
      }).catch(err => console.error('Error sending chat signal', err))
    } else {
      const msgId = 'msg_' + timestamp + '_' + Math.random().toString(36).substring(2, 7)
      for (let i = 0; i < totalChunks; i++) {
        const chunkData = jsonStr.substring(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)
        const chunkObj = {
          id: msgId,
          index: i,
          total: totalChunks,
          chunk: chunkData
        }
        session.value.signal({
          data: JSON.stringify(chunkObj),
          type: 'chat_chunk'
        }).catch(err => console.error('Error sending chat chunk signal', err))
      }
    }
  }

  dispatchHostEvent('chat-message-sent', msgObj)
}

const minimized = ref(false)
const layoutPosition = ref<'right' | 'top'>('right')
const fullscreenStreamId = ref<string | null>(null)

function handleFullscreenChange() {
  const doc = document as any
  const fsElement = doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement
  if (!fsElement) {
    fullscreenStreamId.value = null
    return
  }
  if (fsElement.classList?.contains('grid-video-wrapper')) {
    const streamId = fsElement.getAttribute('data-stream-id')
    if (streamId) {
      fullscreenStreamId.value = streamId
    }
  }
}


const isMobileDevice = computed(() => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
})

const showExitConfirm = ref(false)
const showKickConfirm = ref(false)
const kickTargetId = ref('')
const kickTargetName = ref('')

function handleBeforeUnload(event: BeforeUnloadEvent) {
  if (hasRequestedLeave) return
  event.preventDefault()
  event.returnValue = ''
  return ''
}

const translations = {
  ro: {
    confirmTitle: 'Părăsește apelul',
    confirmMessage: 'Ești sigur că vrei să părăsești această sesiune video?',
    confirmCancel: 'Renunță',
    confirmExit: 'Părăsește',
    shareScreen: 'Partajează ecranul',
    stopShareScreen: 'Oprește partajarea ecranului',
    waitingPermissions: 'Așteptare permisiuni...',
    aloneAlert: 'Ați rămas singur în apel de câteva minute.',
    kickTitle: 'Elimină participantul',
    kickMessage: 'Ești sigur că vrei să îl elimini pe {name} din conferință?',
    kickCancel: 'Renunță',
    kickConfirm: 'Elimină',
    leaveBtn: 'Ieșire',
    microphoneLabel: 'Microfon',
    speakerLabel: 'Difuzor',
    testSpeakerLabel: 'Testare difuzor',
  },
  en: {
    confirmTitle: 'Leave Call',
    confirmMessage: 'Are you sure you want to leave this video session?',
    confirmCancel: 'Cancel',
    confirmExit: 'Leave',
    shareScreen: 'Share screen',
    stopShareScreen: 'Stop sharing screen',
    waitingPermissions: 'Waiting for permissions...',
    aloneAlert: 'You have been alone in the call for a few minutes.',
    kickTitle: 'Kick Participant',
    kickMessage: 'Are you sure you want to kick {name} from the session?',
    kickCancel: 'Cancel',
    kickConfirm: 'Kick',
    leaveBtn: 'Leave',
    microphoneLabel: 'Microphone',
    speakerLabel: 'Speaker',
    testSpeakerLabel: 'Test speaker',
  },
}

const t = computed(() => {
  const parts = (props.lang || localStorage.getItem('user-locale') || document.documentElement.lang || navigator.language || 'ro')
    .toLowerCase()
    .split('-')
  const code = parts[0] || 'ro'
  if (code in translations) {
    return translations[code as keyof typeof translations]
  }
  return translations.ro
})

function clickExitButton() {
  showExitConfirm.value = true
}

function confirmExit() {
  showExitConfirm.value = false
  handleExit()
}

const isDragging = ref(false)
let dragStartX = 0
let dragStartY = 0
let currentTx = 0
let currentTy = 0
let hostRectAtStart: DOMRect | null = null
let dragThresholdPassed = false

function getHostElement(): HTMLElement | null {
  return document.querySelector('video-element') as HTMLElement | null
}

function handleDragStart(e: MouseEvent | TouchEvent) {
  if (!minimized.value) return
  const host = getHostElement()
  if (!host) return

  isDragging.value = true
  dragThresholdPassed = false

  const clientX = 'touches' in e ? (e.touches[0]?.clientX ?? 0) : e.clientX
  const clientY = 'touches' in e ? (e.touches[0]?.clientY ?? 0) : e.clientY

  dragStartX = clientX
  dragStartY = clientY

  hostRectAtStart = host.getBoundingClientRect()

  if ('touches' in e) {
    window.addEventListener('touchmove', handleDragMove, { passive: false })
    window.addEventListener('touchend', handleDragEnd)
  } else {
    window.addEventListener('mousemove', handleDragMove)
    window.addEventListener('mouseup', handleDragEnd)
  }
}

function handleDragMove(e: MouseEvent | TouchEvent) {
  if (!isDragging.value || !hostRectAtStart) return
  const host = getHostElement()
  if (!host) return

  const clientX = 'touches' in e ? (e.touches[0]?.clientX ?? 0) : e.clientX
  const clientY = 'touches' in e ? (e.touches[0]?.clientY ?? 0) : e.clientY

  const deltaX = clientX - dragStartX
  const deltaY = clientY - dragStartY

  if (!dragThresholdPassed && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
    dragThresholdPassed = true
  }

  if (dragThresholdPassed) {
    e.preventDefault()

    const targetLeft = hostRectAtStart.left + deltaX
    const targetTop = hostRectAtStart.top + deltaY

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const hostWidth = hostRectAtStart.width
    const hostHeight = hostRectAtStart.height

    const constrainedLeft = Math.max(0, Math.min(targetLeft, viewportWidth - hostWidth))
    const constrainedTop = Math.max(0, Math.min(targetTop, viewportHeight - hostHeight))

    const appliedDeltaX = constrainedLeft - hostRectAtStart.left
    const appliedDeltaY = constrainedTop - hostRectAtStart.top

    const tx = currentTx + appliedDeltaX
    const ty = currentTy + appliedDeltaY

    host.style.setProperty('transform', `translate(${tx}px, ${ty}px)`, 'important')
  }
}

function handleDragEnd(e: MouseEvent | TouchEvent) {
  isDragging.value = false

  if ('touches' in e) {
    window.removeEventListener('touchmove', handleDragMove)
    window.removeEventListener('touchend', handleDragEnd)
  } else {
    window.removeEventListener('mousemove', handleDragMove)
    window.removeEventListener('mouseup', handleDragEnd)
  }

  if (dragThresholdPassed && hostRectAtStart) {
    const clientX = 'touches' in e ? (e.changedTouches?.[0]?.clientX ?? dragStartX) : e.clientX
    const clientY = 'touches' in e ? (e.changedTouches?.[0]?.clientY ?? dragStartY) : e.clientY

    const deltaX = clientX - dragStartX
    const deltaY = clientY - dragStartY

    const targetLeft = hostRectAtStart.left + deltaX
    const targetTop = hostRectAtStart.top + deltaY

    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const hostWidth = hostRectAtStart.width
    const hostHeight = hostRectAtStart.height

    const constrainedLeft = Math.max(0, Math.min(targetLeft, viewportWidth - hostWidth))
    const constrainedTop = Math.max(0, Math.min(targetTop, viewportHeight - hostHeight))

    const appliedDeltaX = constrainedLeft - hostRectAtStart.left
    const appliedDeltaY = constrainedTop - hostRectAtStart.top

    currentTx += appliedDeltaX
    currentTy += appliedDeltaY
  }

  hostRectAtStart = null

  if (!dragThresholdPassed) {
    toggleMinimize()
  }
}

function toggleMinimize() {
  minimized.value = !minimized.value
  dispatchHostEvent('minimize-toggle', { minimized: minimized.value })
  if (!minimized.value) {
    const host = getHostElement()
    if (host) {
      host.style.removeProperty('transform')
    }
    currentTx = 0
    currentTy = 0
  }
}

function toggleLayout() {
  layoutPosition.value = layoutPosition.value === 'right' ? 'top' : 'right'
}

function handleMainVideoDblClick(e: MouseEvent) {
  const doc = document as any
  const el = (document.querySelector('video-element') || document.getElementById('main-container')) as any
  if (!el) return

  if (doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement) {
    if (doc.exitFullscreen) {
      doc.exitFullscreen().catch(() => {})
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen()
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen()
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen()
    }
  } else {
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {})
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen()
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen()
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen()
    } else {
      // iPhone / iOS Safari fallback (direct fullscreen on <video> element)
      const container = e.currentTarget as HTMLElement | null
      const video = container?.querySelector('video') as any
      if (video && video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen()
      }
    }
  }
}

function handleFullscreenClick(e: MouseEvent) {
  const doc = document as any
  const button = e.currentTarget as HTMLElement | null
  const wrapper = button?.closest('.grid-video-wrapper') as HTMLElement | null
  const el = (wrapper || document.querySelector('video-element') || document.getElementById('main-container')) as any
  if (!el) return

  if (doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement) {
    if (doc.exitFullscreen) {
      doc.exitFullscreen().catch(() => {})
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen()
    } else if (doc.mozCancelFullScreen) {
      doc.mozCancelFullScreen()
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen()
    }
  } else {
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {})
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen()
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen()
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen()
    } else {
      const video = el.querySelector('video') as any
      if (video && video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen()
      }
    }
  }
}

function forceMuteParticipant(userId: string) {
  if (session.value) {
    const cleanId = userId.replace('-screen', '')
    session.value.signal({
      type: 'force-mute',
      data: cleanId
    }).catch((err) => console.error('Error sending force-mute signal:', err))
  }
}

function forceKickParticipant(userId: string) {
  if (session.value) {
    const cleanId = userId.replace('-screen', '')
    session.value.signal({
      type: 'force-kick',
      data: cleanId
    }).catch((err) => console.error('Error sending force-kick signal:', err))
  }
}


function requestKickParticipant(connection: any) {
  if (!connection) return
  const userId = getUserIdOfConnection(connection)
  if (!userId) return
  
  kickTargetId.value = userId
  kickTargetName.value = getUserNameOfConnection(connection)
  showKickConfirm.value = true
}

function confirmKickParticipant() {
  if (kickTargetId.value) {
    forceKickParticipant(kickTargetId.value)
  }
  showKickConfirm.value = false
  kickTargetId.value = ''
  kickTargetName.value = ''
}

function joinSession() {
  hasRequestedLeave = false
  awaitingTokenRefresh.value = false
  expiredToken.value = null
  retryAttempts.value = 0
  clearRetryTimer()
  const localSession = createSession()
  connectToSession(localSession)
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('unload', leaveSession)
  window.addEventListener('pagehide', leaveSession)
}

async function reShowDevices() {
  if (selectedCameraId.value) {
    await updateCameraPreview()
  }

  if (selectedMicId.value) {
    await updateMicMeter()
  }
}

function leaveSession() {
  hasJoinedSession.value = false
  stopScreenShare()
  stopMuteSpeakingDetector()
  showMuteWarning.value = false
  hasRequestedLeave = true
  awaitingTokenRefresh.value = false
  expiredToken.value = null
  retryAttempts.value = 0
  clearRetryTimer()
  if (lastCameraSwitchStream) {
    lastCameraSwitchStream.getTracks().forEach((t) => t.stop())
    lastCameraSwitchStream = null
  }
  if (lastMicSwitchStream) {
    lastMicSwitchStream.getTracks().forEach((t) => t.stop())
    lastMicSwitchStream = null
  }
  if (session.value) session.value.disconnect()

  participants.value = []
  isConnectedPromiseResolved = false

  // Empty all properties...
  session.value = undefined
  mainStreamManager.value = undefined
  publisher.value = undefined
  subscribers.value = []
  remoteCameraStreamIds.value = []
  meetingDurationSeconds.value = 0
  hasMeetingStarted.value = false
  meetingStartTimestamp.value = null
  aloneDurationSeconds.value = 0

  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('unload', leaveSession)
  window.removeEventListener('pagehide', leaveSession)
}

function handleExit() {
  leaveSession()
  dispatchHostEvent('video-close')
}

/**
 * Leave the current session and join the session for the given token.
 * Used when the user answers a different call (e.g. already in a call and accepts an incoming one).
 */
function switchToNewSession(newToken: string) {
  if (!newToken || newToken === lastTokenUsed.value) return
  if (!session.value) {
    // Not in a session yet; let the existing reconnection path or join handle it
    return
  }

  hasRequestedLeave = true
  if (session.value) session.value.disconnect()
  session.value = undefined
  mainStreamManager.value = undefined
  publisher.value = undefined
  subscribers.value = []

  clearRetryTimer()
  retryAttempts.value = 0
  expiredToken.value = null
  awaitingTokenRefresh.value = false
  hasRequestedLeave = false
  lastTokenUsed.value = null

  const localSession = createSession()
  connectToSession(localSession)
}

function createSession() {
  const localSession = OV.initSession()

  localSession.on('streamCreated', ({ stream }) => {
    if (screenSession.value && screenSession.value.connection && stream.connection.connectionId === screenSession.value.connection.connectionId) {
      console.log("Ignoring own screen share stream by connectionId")
      return
    }
    if (localUserId.value) {
      try {
        const parsed = JSON.parse(stream.connection.data)
        if (parsed.userId === localUserId.value + '-screen') {
          console.log("Ignoring own screen share stream by userId-screen")
          return
        }
      } catch {
        // ignore
      }
    }

    const subscriber = localSession.subscribe(stream, undefined) as Subscriber
    subscribers.value.push(subscriber)
    subscriberMutedMap.value[stream.streamId] = !stream.audioActive
    subscriberVideoOffMap.value[stream.streamId] = !stream.videoActive
    if (stream.typeOfVideo === 'SCREEN') {
      if (hasJoinedSession.value) {
        dispatchHostEvent('screen-share-started')
      }
    } else {
      remoteCameraStreamIds.value.push(stream.streamId)
    }
  })

  localSession.on('streamDestroyed', ({ stream }: StreamEvent) => {
    const index = subscribers.value.findIndex(
      (subscriber) => subscriber.stream?.streamId === stream.streamId,
    )
    if (index !== -1) {
      subscribers.value.splice(index, 1)
    }
    const rcIndex = remoteCameraStreamIds.value.indexOf(stream.streamId)
    if (rcIndex !== -1) {
      remoteCameraStreamIds.value.splice(rcIndex, 1)
    }
    const pinIdx = pinnedStreamIds.value.indexOf(stream.streamId)
    if (pinIdx !== -1) {
      pinnedStreamIds.value.splice(pinIdx, 1)
    }
    delete subscriberMutedMap.value[stream.streamId]
    delete subscriberVideoOffMap.value[stream.streamId]
    delete speakingMap.value[stream.connection.connectionId]
    
    if (mainStreamManager.value && mainStreamManager.value.stream?.streamId === stream.streamId) {
      mainStreamManager.value = publisher.value
      selectedStreamId.value = null
    }
    if (stream.typeOfVideo === 'SCREEN') {
      dispatchHostEvent('screen-share-stopped')
    }
  })

  localSession.on('streamPropertyChanged', (event: any) => {
    const streamId = event.stream?.streamId
    if (!streamId) return
    if (event.changedProperty === 'audioActive') {
      subscriberMutedMap.value[streamId] = !event.newValue
    } else if (event.changedProperty === 'videoActive') {
      subscriberVideoOffMap.value[streamId] = !event.newValue
    }
  })

  localSession.on('publisherStartSpeaking', (event: any) => {
    const connId = event.connection?.connectionId
    if (connId) speakingMap.value[connId] = true
  })

  localSession.on('publisherStopSpeaking', (event: any) => {
    const connId = event.connection?.connectionId
    if (connId) speakingMap.value[connId] = false
  })

  const chunkBuffer: Record<string, { total: number; chunks: string[] }> = {}

  localSession.on('signal:chat_chunk', (event: any) => {
    if (event.from && localSession.connection && event.from.connectionId === localSession.connection.connectionId) {
      return
    }
    try {
      const payload = JSON.parse(event.data)
      const { id, index, total, chunk } = payload
      if (!chunkBuffer[id]) {
        chunkBuffer[id] = { total, chunks: new Array(total) }
      }
      chunkBuffer[id].chunks[index] = chunk

      const receivedCount = chunkBuffer[id].chunks.filter(c => c !== undefined).length
      if (receivedCount === total) {
        const fullJson = chunkBuffer[id].chunks.join('')
        delete chunkBuffer[id]
        const data = JSON.parse(fullJson)
        const rMsg: ChatMessage = {
          text: data.text || '',
          senderName: data.senderName || 'Participant',
          senderId: data.senderId || '',
          timestamp: data.timestamp || Date.now(),
          replyTo: data.replyTo || undefined,
          attachment: data.attachment || undefined
        }
        chatMessages.value.push(rMsg)
        dispatchHostEvent('chat-message-received', rMsg)
        if (!isChatOpen.value && !isExternalChatOpen.value) {
          unreadChatCount.value++
          playChatNotificationSound()
        } else {
          unreadChatCount.value = 0
          scrollToBottomChat()
        }
      }
    } catch (e) {
      console.error('Failed to parse chat chunk signal', e)
    }
  })

  localSession.on('signal:chat', (event: any) => {
    if (event.from && localSession.connection && event.from.connectionId === localSession.connection.connectionId) {
      return
    }
    try {
      const data = JSON.parse(event.data)
      const rMsg: ChatMessage = {
        text: data.text || '',
        senderName: data.senderName || 'Participant',
        senderId: data.senderId || '',
        timestamp: data.timestamp || Date.now(),
        replyTo: data.replyTo || undefined,
        attachment: data.attachment || undefined
      }
      chatMessages.value.push(rMsg)
      dispatchHostEvent('chat-message-received', rMsg)
      if (!isChatOpen.value && !isExternalChatOpen.value) {
        unreadChatCount.value++
        playChatNotificationSound()
      } else {
        unreadChatCount.value = 0
        scrollToBottomChat()
      }
    } catch (e) {
      console.error('Failed to parse chat signal', e)
    }
  })

  localSession.on('connectionCreated', ({ connection }) => {
    addParticipant(connection)

    const { userId } = parseConnectionData(connection.data)
    const isLocal = localSession.connection && connection.connectionId === localSession.connection.connectionId
    const isScreen = userId.endsWith('-screen')

    if (isConnectedPromiseResolved && !isLocal && !isScreen) {
      dispatchHostEvent('participant-joined')
    }

    if (
      hasMeetingStarted.value &&
      meetingStartTimestamp.value &&
      localSession.connection &&
      connection.connectionId !== localSession.connection.connectionId
    ) {
      localSession.signal({
        type: 'timer-sync',
        data: meetingStartTimestamp.value.toString(),
        to: [connection]
      }).catch((err) => console.error('Error sending targeted timer-sync signal:', err))
    }
  })

  localSession.on('connectionDestroyed', ({ connection }) => {
    removeParticipant(connection)

    const { userId } = parseConnectionData(connection.data)
    const isLocal = localSession.connection && connection.connectionId === localSession.connection.connectionId
    const isScreen = userId.endsWith('-screen')

    if (isConnectedPromiseResolved && !isLocal && !isScreen) {
      dispatchHostEvent('participant-left')
    }
  })

  localSession.on('signal:timer-sync', (event: any) => {
    if (event.data) {
      const ts = parseInt(event.data, 10)
      if (!isNaN(ts)) {
        if (!meetingStartTimestamp.value || ts < meetingStartTimestamp.value) {
          meetingStartTimestamp.value = ts
          hasMeetingStarted.value = true
        }
      }
    }
  })

  localSession.on('signal:force-mute', (event: any) => {
    if (event.data && localUserId.value && event.data === localUserId.value) {
      if (!isMicMuted.value) {
        toggleMic()
      }
    }
  })

  localSession.on('signal:force-kick', (event: any) => {
    if (event.data && localUserId.value && event.data === localUserId.value) {
      handleExit()
    }
  })

  localSession.on('exception', (event) => {
    console.warn(event.name, event.message)
  })

  localSession.on('sessionDisconnected', () => {
    if (hasRequestedLeave) return

    mainStreamManager.value = undefined
    publisher.value = undefined
    subscribers.value = []
    remoteCameraStreamIds.value = []
    participants.value = []
    isConnectedPromiseResolved = false
    meetingDurationSeconds.value = 0
    hasMeetingStarted.value = false
    meetingStartTimestamp.value = null
    aloneDurationSeconds.value = 0
    session.value = undefined

    requestTokenRefresh()
  })

  session.value = localSession
  return localSession
}

async function connectToSession(localSession: Session) {
  if (isConnecting.value || hasRequestedLeave) return
  if (awaitingTokenRefresh.value && props.sessionToken === lastTokenUsed.value) return
  hasJoinedSession.value = false
  isConnecting.value = true

  try {
    const token = props.sessionToken
    lastTokenUsed.value = token
    await localSession.connect(token)
    // Add our own connection to the participants list
    if (localSession.connection) {
      addParticipant(localSession.connection)
    }
    isConnectedPromiseResolved = true
    awaitingTokenRefresh.value = false
    retryAttempts.value = 0

    if (hasMicAccess.value || hasCameraAccess.value) {
      const publisherProps: PublisherProperties = {
        audioSource: hasMicAccess.value ? selectedMicId.value : false,
        videoSource: hasCameraAccess.value ? selectedCameraId.value : false,
        publishAudio: hasMicAccess.value && !isMicMuted.value,
        publishVideo: hasCameraAccess.value && !isVideoOff.value,
        resolution: '640x480',
        frameRate: 30,
        insertMode: 'APPEND',
        mirror: false,
      }

      const localPublisher: Publisher = OV.initPublisher(undefined, publisherProps)
      mainStreamManager.value = localPublisher
      publisher.value = localPublisher

      if (session.value !== undefined && publisher.value !== undefined) {
        session.value.publish(publisher.value as Publisher)
      }
    } else {
      console.log('No mic or camera access — joining as subscriber-only participant.')
    }
    dispatchHostEvent('session-joined')
    hasJoinedSession.value = true
    if (isMicMuted.value) {
      startMuteSpeakingDetector()
    }

  } catch (error: any) {
    const errorCode = error?.code
    const errorMessage = error?.message || ''

    if (hasRequestedLeave) return

    if (
      errorCode === 404 ||
      errorCode === 401 ||
      errorCode === 202 ||
      /does not exist/i.test(errorMessage) ||
      /cannot be found/i.test(errorMessage)
    ) {
      expiredToken.value = props.sessionToken
      requestTokenRefresh()
      session.value = undefined
      await nextTick()
      await reShowDevices()
      return
    }

    console.warn('There was an error connecting to the session:', errorCode, errorMessage)
    requestTokenRefresh()
  } finally {
    isConnecting.value = false
  }
}

function requestTokenRefresh() {
  if (hasRequestedLeave || awaitingTokenRefresh.value || retryTimer) return
  if (retryAttempts.value >= MAX_RETRY_ATTEMPTS) {
    handleExit()
    return
  }
  retryAttempts.value += 1
  awaitingTokenRefresh.value = true
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    if (hasRequestedLeave) return
    dispatchHostEvent('video-token-needed')
  }, RETRY_DELAY_MS)
}

function clearRetryTimer() {
  if (retryTimer) {
    window.clearTimeout(retryTimer)
    retryTimer = null
  }
}


// Refresh device lists, ensuring we prompt for permissions so cameras enumerate properly
async function refreshDevices() {
  let tempStream: MediaStream | null = null
  isWaitingForPermissions.value = true
  try {
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      }
    } catch (error) {
      console.warn('Initial getUserMedia with both audio and video failed, trying fallback...', error)
      try {
        if (navigator.mediaDevices?.getUserMedia) {
          tempStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        }
      } catch (audioError) {
        console.warn('Fallback getUserMedia with audio failed, trying video only...', audioError)
        try {
          if (navigator.mediaDevices?.getUserMedia) {
            tempStream = await navigator.mediaDevices.getUserMedia({ video: true })
          }
        } catch (videoError) {
          console.warn('Fallback getUserMedia with video failed as well.', videoError)
        }
      }
    }

    const all = await navigator.mediaDevices.enumerateDevices()
    const videoInputs = all.filter((d) => d.kind === 'videoinput')
    const audioInputs = all.filter((d) => d.kind === 'audioinput')
    const audioOutputs = all.filter((d) => d.kind === 'audiooutput')

    cameras.value = videoInputs as MediaDeviceInfo[]
    microphones.value = audioInputs as MediaDeviceInfo[]
    speakers.value = audioOutputs as MediaDeviceInfo[]

    const speakerExists = speakers.value.some((spk) => spk.deviceId === selectedSpeakerId.value)
    if ((!speakerExists || !selectedSpeakerId.value) && speakers.value.length > 0 && speakers.value[0]) {
      selectedSpeakerId.value = speakers.value[0].deviceId
    }
    applyAudioOutputDevice()

    if (!hasCameraAccess.value) {
      isVideoOff.value = true
    }
    if (!hasMicAccess.value) {
      isMicMuted.value = true
    }

    const cameraExists = cameras.value.some((cam) => cam.deviceId === selectedCameraId.value)
    if ((!cameraExists || !selectedCameraId.value) && cameras.value.length > 0 && cameras.value[0]) {
      selectedCameraId.value = cameras.value[0].deviceId
    }
    if (selectedCameraId.value && hasCameraAccess.value) {
      await updateCameraPreview()
    }

    const micExists = microphones.value.some((mic) => mic.deviceId === selectedMicId.value)
    if ((!micExists || !selectedMicId.value) && microphones.value.length > 0 && microphones.value[0]) {
      selectedMicId.value = microphones.value[0].deviceId
    }
    if (selectedMicId.value && hasMicAccess.value) {
      await updateMicMeter()
    }
  } finally {
    // Stop any temp tracks so we don't keep devices active
    if (tempStream) {
      tempStream.getTracks().forEach((t) => t.stop())
    }
    isWaitingForPermissions.value = false
  }
}

function handleDeviceChange() {
  refreshDevices()
}

function setupClickOutsideToCloseDropdowns() {
  const close = () => {
    closeDeviceDropdowns()
    document.removeEventListener('click', close)
  }
  nextTick(() => {
    setTimeout(() => document.addEventListener('click', close), 0)
  })
}

watch([showCameraDropdown, showMicDropdown, showSpeakerDropdown], ([cam, mic, spk]) => {
  if (cam || mic || spk) {
    setupClickOutsideToCloseDropdowns()
  }
})

watch([subscribers, publisher], () => {
  nextTick(() => {
    setTimeout(applyAudioOutputDevice, 200)
  })
}, { deep: true })

function handleExternalChatMessage(event: any) {
  const msgObj = event.detail
  if (msgObj && (msgObj.text || msgObj.attachment)) {
    const exists = chatMessages.value.some(
      m => m.timestamp === msgObj.timestamp && m.senderId === msgObj.senderId && (m.text === msgObj.text || (m.attachment && msgObj.attachment && m.attachment.url === msgObj.attachment.url))
    )
    if (!exists) {
      chatMessages.value.push({
        text: msgObj.text || '',
        senderName: msgObj.senderName || 'Participant',
        senderId: msgObj.senderId || '',
        timestamp: msgObj.timestamp || Date.now(),
        replyTo: msgObj.replyTo,
        attachment: msgObj.attachment
      })
      const isFromMe = msgObj.senderId === 'me' || (!!localUserId.value && String(msgObj.senderId) === String(localUserId.value))
      if (!isChatOpen.value && !isExternalChatOpen.value && !isFromMe) {
        unreadChatCount.value++
        playChatNotificationSound()
      } else {
        unreadChatCount.value = 0
        scrollToBottomChat()
      }
    }
  }
}

onMounted(async () => {
  window.addEventListener('external-video-chat-message', handleExternalChatMessage)
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
  document.addEventListener('mozfullscreenchange', handleFullscreenChange)
  document.addEventListener('MSFullscreenChange', handleFullscreenChange)

  // First population
  await refreshDevices()
  // Keep lists fresh when devices plug/unplug
  if (navigator.mediaDevices && 'ondevicechange' in navigator.mediaDevices) {
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)
  }

  // Start conference duration timer
  timerInterval = window.setInterval(() => {
    if (!hasMeetingStarted.value && humanParticipantCount.value >= 2) {
      startMeetingTimer()
    }
    if (hasMeetingStarted.value) {
      if (meetingStartTimestamp.value) {
        meetingDurationSeconds.value = Math.floor((Date.now() - meetingStartTimestamp.value) / 1000)
      } else {
        meetingDurationSeconds.value++
      }
    }

    // Check if user is alone in the call
    if (session.value && humanParticipantCount.value === 1) {
      aloneDurationSeconds.value++
      if (aloneDurationSeconds.value === 300) {
        playNotificationSound()
        setTimeout(() => {
          window.alert(t.value.aloneAlert)
        }, 500)
      }
    } else {
      aloneDurationSeconds.value = 0
    }
  }, 1000)
})

watch(
  () => props.sessionToken,
  (newToken) => {
    if (!newToken) return
    if (expiredToken.value && newToken !== expiredToken.value) {
      expiredToken.value = null
    }
    // Already in a call and token changed (e.g. user answered another call): leave current and join new
    if (session.value && newToken !== lastTokenUsed.value) {
      switchToNewSession(newToken)
      return
    }
    if (awaitingTokenRefresh.value && newToken !== lastTokenUsed.value) {
      awaitingTokenRefresh.value = false
      hasRequestedLeave = false
      const localSession = createSession()
      connectToSession(localSession)
    }
  },
)

async function updateCameraPreview() {
  // Stop previous preview
  if (previewPublisher) {
    previewPublisher.stream
      .getMediaStream()
      .getTracks()
      .forEach((t) => t.stop())
    previewPublisher = null
  }

  // Clear previous video DOM
  if (previewContainer.value) {
    previewContainer.value.innerHTML = ''
  }

  if (isVideoOff.value || !selectedCameraId.value) return

  previewPublisher = OV.initPublisher(previewContainer.value, {
    videoSource: selectedCameraId.value,
    publishVideo: true,
    publishAudio: false,
    audioSource: false,
    mirror: false,
  })
}

// 🟡 MICROPHONE VOLUME METER
async function updateMicMeter() {
  // stop previous audio stream
  if (micStream) {
    micStream.getTracks().forEach((t) => t.stop())
    micStream = null
  }

  if (audioContext) {
    await audioContext.close()
    audioContext = null
  }

  micVolume.value = 0

  if (isMicMuted.value || !selectedMicId.value) return

  micStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: { exact: selectedMicId.value as string },
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
      sampleRate: 48000,
      latency: 0,
    } as MediaTrackConstraints,
  })

  audioContext = new AudioContext({ latencyHint: 'interactive' })
  await audioContext.resume()
  const source = audioContext.createMediaStreamSource(micStream)

  analyser = audioContext.createAnalyser()
  source.connect(analyser)

  analyser.smoothingTimeConstant = 0.0
  analyser.fftSize = 128
  const timeDataF32 = new Float32Array(analyser.fftSize)

  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }

  let peak = 0
  const decay = 0.95

  const tick = () => {
    if (!analyser) return
    analyser.getFloatTimeDomainData(timeDataF32)

    let max = 0
    for (let i = 0; i < timeDataF32.length; i++) {
      const val = timeDataF32[i]
      if (val !== undefined) {
        max = Math.max(max, Math.abs(val))
      }
    }

    peak = Math.max(max, peak * decay)
    micVolume.value = Math.min(100, peak * 200)

    rafId = requestAnimationFrame(tick)
  }
  tick()
}

async function startMuteSpeakingDetector() {
  stopMuteSpeakingDetector()

  try {
    const micId = typeof selectedMicId.value === 'string' ? selectedMicId.value : ''
    const constraints: MediaStreamConstraints = {
      audio: micId ? { deviceId: { exact: micId } } : true,
    }
    muteDetectorStream = await navigator.mediaDevices.getUserMedia(constraints)

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioCtx) return

    muteDetectorAudioCtx = new AudioCtx()
    await muteDetectorAudioCtx.resume()

    const source = muteDetectorAudioCtx.createMediaStreamSource(muteDetectorStream)
    muteDetectorAnalyser = muteDetectorAudioCtx.createAnalyser()
    source.connect(muteDetectorAnalyser)

    muteDetectorAnalyser.smoothingTimeConstant = 0.4
    muteDetectorAnalyser.fftSize = 256
    const dataArray = new Float32Array(muteDetectorAnalyser.fftSize)

    const tick = () => {
      if (!muteDetectorAnalyser || !isMicMuted.value || !session.value) {
        return
      }

      muteDetectorAnalyser.getFloatTimeDomainData(dataArray)
      let maxVal = 0
      for (let i = 0; i < dataArray.length; i++) {
        const val = dataArray[i]
        if (val !== undefined) {
          maxVal = Math.max(maxVal, Math.abs(val))
        }
      }

      if (maxVal > 0.04) {
        muteConsecutiveSpeakingFrames++
      } else {
        muteConsecutiveSpeakingFrames = 0
      }

      if (muteConsecutiveSpeakingFrames >= 10) {
        const now = Date.now()
        if (!showMuteWarning.value && now - lastMuteWarningTime > 5000) {
          showMuteWarning.value = true
          playMuteWarningSound()
          lastMuteWarningTime = now
          muteConsecutiveSpeakingFrames = 0
          if (muteWarningTimeout) clearTimeout(muteWarningTimeout)
          muteWarningTimeout = setTimeout(() => {
            showMuteWarning.value = false
          }, 3500)
        }
      }

      muteDetectorRafId = requestAnimationFrame(tick)
    }

    tick()
  } catch (err) {
    console.warn('[MuteDetector] Failed to capture microphone for mute speaking detection:', err)
  }
}

function stopMuteSpeakingDetector() {
  if (muteDetectorRafId) {
    cancelAnimationFrame(muteDetectorRafId)
    muteDetectorRafId = null
  }
  if (muteDetectorStream) {
    muteDetectorStream.getTracks().forEach((t) => t.stop())
    muteDetectorStream = null
  }
  if (muteDetectorAudioCtx) {
    muteDetectorAudioCtx.close().catch(() => {})
    muteDetectorAudioCtx = null
  }
  muteDetectorAnalyser = null
  muteConsecutiveSpeakingFrames = 0
}

function toggleMic() {
  isMicMuted.value = !isMicMuted.value
  if (publisher.value) {
    publisher.value.publishAudio(!isMicMuted.value)
  }

  showMuteWarning.value = false
  if (muteWarningTimeout) {
    clearTimeout(muteWarningTimeout)
    muteWarningTimeout = null
  }

  if (isMicMuted.value) {
    if (micStream) {
      micStream.getTracks().forEach((t) => t.stop())
      micStream = null
    }
    if (audioContext) {
      audioContext.close()
      audioContext = null
    }
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    micVolume.value = 0
    if (session.value) {
      startMuteSpeakingDetector()
    }
    return
  } else {
    stopMuteSpeakingDetector()
  }

  if (selectedMicId.value) {
    updateMicMeter()
  }
}

function toggleVideo() {
  isVideoOff.value = !isVideoOff.value
  if (publisher.value) {
    publisher.value.publishVideo(!isVideoOff.value)
  }

  if (isVideoOff.value) {
    if (previewPublisher) {
      previewPublisher.stream
        .getMediaStream()
        .getTracks()
        .forEach((t) => t.stop())
      previewPublisher = null
    }
    if (previewContainer.value) {
      previewContainer.value.innerHTML = ''
    }
    return
  }

  if (selectedCameraId.value) {
    updateCameraPreview()
  }
}

function closeDeviceDropdowns() {
  showCameraDropdown.value = false
  showMicDropdown.value = false
  showSpeakerDropdown.value = false
}

async function switchCamera(deviceId: string) {
  if (!publisher.value || !session.value) return
  try {
    if (lastCameraSwitchStream) {
      lastCameraSwitchStream.getTracks().forEach((t) => t.stop())
      lastCameraSwitchStream = null
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: deviceId ? { exact: deviceId } : undefined },
    })
    const videoTrack = stream.getVideoTracks()[0]
    if (!videoTrack) return
    await publisher.value.replaceTrack(videoTrack)
    selectedCameraId.value = deviceId
    lastCameraSwitchStream = stream
    showCameraDropdown.value = false
  } catch (err) {
    console.warn('Failed to switch camera:', err)
    showCameraDropdown.value = false
  }
}

async function switchMicrophone(deviceId: string) {
  if (!publisher.value || !session.value) return
  try {
    if (lastMicSwitchStream) {
      lastMicSwitchStream.getTracks().forEach((t) => t.stop())
      lastMicSwitchStream = null
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: deviceId ? { exact: deviceId } : undefined },
    })
    const audioTrack = stream.getAudioTracks()[0]
    if (!audioTrack) return
    await publisher.value.replaceTrack(audioTrack)
    selectedMicId.value = deviceId
    lastMicSwitchStream = stream
    showMicDropdown.value = false
    if (!isMicMuted.value) {
      updateMicMeter()
    }
  } catch (err) {
    console.warn('Failed to switch microphone:', err)
    showMicDropdown.value = false
  }
}

onBeforeUnmount(() => {
  window.removeEventListener('external-video-chat-message', handleExternalChatMessage)
  stopScreenShare()
  if (session.value) {
    session.value.disconnect()
    session.value = undefined
  }
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
  document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
  document.removeEventListener('MSFullscreenChange', handleFullscreenChange)

  awaitingTokenRefresh.value = false
  clearRetryTimer()
  if (timerInterval) {
    window.clearInterval(timerInterval)
    timerInterval = null
  }
  if (previewPublisher) {
    previewPublisher.stream
      .getMediaStream()
      .getTracks()
      .forEach((t) => t.stop())
  }

  if (micStream) {
    micStream.getTracks().forEach((t) => t.stop())
  }

  if (audioContext) audioContext.close()
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = null
  }

  if (navigator.mediaDevices && 'ondevicechange' in navigator.mediaDevices) {
    navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange)
  }
})

const formatCameraLabel = (camera: MediaDeviceInfo) => {
  if (!camera.label) {
    return `Camera ${camera.deviceId}`
  }

  return camera.label.replace(/\s*\([^)]*\)/g, '').trim()
}

const formatMicLabel = (microphone: MediaDeviceInfo) => {
  if (!microphone.label) {
    return `Microphone ${microphone.deviceId}`
  }

  return microphone.label.replace(/\s*\([^)]*\)/g, '').trim()
}

const formatSpeakerLabel = (speaker: MediaDeviceInfo) => {
  if (!speaker.label) {
    return `Speaker ${speaker.deviceId}`
  }
  return speaker.label.replace(/\s*\([^)]*\)/g, '').trim()
}

function switchSpeaker(deviceId: string) {
  selectedSpeakerId.value = deviceId
  localStorage.setItem('selected-speaker-id', deviceId)
  applyAudioOutputDevice()
  showSpeakerDropdown.value = false
}

function applyAudioOutputDevice() {
  const deviceId = selectedSpeakerId.value
  if (!deviceId) return

  const shadowRoot = document.querySelector('video-element')?.shadowRoot
  const queryRoot = shadowRoot || document
  const mediaElements = queryRoot.querySelectorAll('video, audio')
  mediaElements.forEach((el: any) => {
    if (typeof el.setSinkId === 'function') {
      el.setSinkId(deviceId).catch((err: any) => {
        console.warn('Error setting sinkId for element:', err)
      })
    }
  })
}

function testSpeaker() {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
    if (selectedSpeakerId.value && typeof (audioCtx as any).setSinkId === 'function') {
      (audioCtx as any).setSinkId(selectedSpeakerId.value).then(() => {
        playBeep(audioCtx)
      }).catch((err: any) => {
        console.warn('setSinkId failed on AudioContext, playing on default:', err)
        playBeep(audioCtx)
      })
    } else {
      playBeep(audioCtx)
    }
  } catch (err) {
    console.error('Failed to test speaker:', err)
  }
}

function playBeep(audioCtx: AudioContext) {
  const osc = audioCtx.createOscillator()
  const gain = audioCtx.createGain()
  osc.connect(gain)
  gain.connect(audioCtx.destination)
  osc.type = 'sine'
  osc.frequency.setValueAtTime(440, audioCtx.currentTime)
  gain.gain.setValueAtTime(0.3, audioCtx.currentTime)
  gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.5)
  osc.start(audioCtx.currentTime)
  osc.stop(audioCtx.currentTime + 0.5)
}
</script>

<template>
  <div id="main-container" class="container">
    <span id="session-token">{{ sessionToken }}</span>
    <div id="join" v-if="!session">
      <div id="join-dialog" class="jumbotron vertical-center">
        <div class="device-setup">
          <div class="device-preview">
            <div class="preview-notice">Preview</div>
            <div class="camera-preview-wrapper">
              <div
                v-if="isConnectionOverlayVisible"
                class="connection-overlay"
                aria-live="polite"
              >
                <div class="connection-overlay__content">
                  <div
                    v-if="isReconnecting"
                    class="connection-overlay__spinner"
                    aria-hidden="true"
                  ></div>
                  <div class="connection-overlay__label">{{ connectionOverlayLabel }}</div>
                </div>
              </div>
              <div ref="previewContainer" class="camera-preview"></div>
              <div class="mic-meter">
                <div class="bar" :style="{ width: micVolume + '%' }"></div>
                <div class="mic-label">Microphone check</div>
              </div>
            </div>
            <div class="preview-controls">
              <button
                class="preview-toggle"
                type="button"
                :aria-label="micToggleLabel"
                :title="micToggleLabel"
                @click="toggleMic"
              >
                <MicOff v-if="isMicMuted" class="toggle-icon" />
                <MicOn v-else class="toggle-icon" />
              </button>
              <button
                class="preview-toggle"
                type="button"
                :aria-label="videoToggleLabel"
                :title="videoToggleLabel"
                @click="toggleVideo"
              >
                <CamOff v-if="isVideoOff" class="toggle-icon" />
                <CamOn v-else class="toggle-icon" />
              </button>
            </div>
          </div>
          <div class="device-selector">
            <select class="mic-selector" v-model="selectedMicId" @change="updateMicMeter">
              <option v-for="mic in microphones" :key="mic.deviceId" :value="mic.deviceId">
                {{ formatMicLabel(mic) }}
              </option>
            </select>
            <select
              class="speaker-selector"
              v-model="selectedSpeakerId"
              @change="switchSpeaker(selectedSpeakerId)"
            >
              <option v-for="spk in speakers" :key="spk.deviceId" :value="spk.deviceId">
                {{ formatSpeakerLabel(spk) }}
              </option>
            </select>
            <select
              class="camera-selector"
              v-model="selectedCameraId"
              @change="updateCameraPreview"
            >
              <option v-for="cam in cameras" :key="cam.deviceId" :value="cam.deviceId">
                {{ formatCameraLabel(cam) }}
              </option>
            </select>
          </div>
        </div>
        <div class="button-container">
          <div class="join-actions">
            <button class="join" :disabled="isWaitingForPermissions" @click="joinSession()">
              {{ isWaitingForPermissions ? t.waitingPermissions : 'Join!' }}
            </button>
            <button
              class="preview-toggle exit-button"
              type="button"
              aria-label="Exit"
              title="Exit"
              @click="handleExit"
            >
              <ExitDoor class="toggle-icon" />
              <span class="exit-label">Exit</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div id="session" v-if="session">
      <div class="session-main-area" :class="{ 'layout-top': layoutPosition === 'top' }">
        <div id="main-video" class="col-md-6" :class="{ 'has-subscribers': sidebarStreams.length > 0 }">
          <div
            class="video-grid"
            :class="'grid-' + gridStreams.length"
            :style="{ '--cols': gridLayout.cols, '--rows': gridLayout.rows }"
          >
            <div
              v-for="item in gridStreams"
              :key="item.id"
              class="grid-video-wrapper"
              :class="{
                'speaking-indicator': speakingMap[item.connection?.connectionId ?? ''],
                'is-screen-share': item.isScreen
              }"
              :data-stream-id="item.streamManager?.stream?.streamId"
              @dblclick="handleMainVideoDblClick"
            >
              <user-video :stream-manager="item.streamManager" :connection="item.connection" />
              <MicOn
                v-if="speakingMap[item.connection?.connectionId ?? '']"
                class="mute-overlay speaking-mic-overlay"
              />
              <MicOff v-else-if="isMicMuted && item.streamManager === publisher" class="mute-overlay" />
              <MicOff
                v-else-if="item.streamManager && item.streamManager !== publisher && subscriberMutedMap[item.streamManager.stream?.streamId ?? '']"
                class="mute-overlay"
              />
              <CamOff v-if="isVideoOff && item.streamManager === publisher" class="cam-overlay" />
              <CamOff
                v-else-if="item.streamManager && item.streamManager !== publisher && subscriberVideoOffMap[item.streamManager.stream?.streamId ?? '']"
                class="cam-overlay"
              />

              <!-- Persistent pin indicator (non-interactive, top-left) -->
              <div
                v-if="pinnedStreamIds.includes(item.id)"
                class="persistent-pin-indicator"
                title="Pinned"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="persistent-pin-icon">
                  <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2z"></path>
                </svg>
              </div>

              <!-- Pin/Unpin action overlay -->
              <button
                class="pin-action-btn"
                :class="{ 'pin-action-btn--pinned': pinnedStreamIds.includes(item.id) }"
                type="button"
                :title="pinnedStreamIds.includes(item.id) ? 'Unpin' : 'Pin'"
                @click.stop="togglePin(item.id)"
              >
                <svg v-if="pinnedStreamIds.includes(item.id)" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pin-btn-icon">
                  <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2z"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="pin-btn-icon">
                  <line x1="12" y1="17" x2="12" y2="22"></line>
                  <path d="M5 17h14v-1.76a2 2 0 0 0-.44-1.24l-2.78-3.5A2 2 0 0 1 15 9.24V5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v4.24c0 .43-.14.85-.4 1.21l-2.78 3.51A2 2 0 0 0 5 15.24Z"></path>
                </svg>
              </button>

              <!-- Host action overlays (Mute/Kick) -->
              <button
                v-if="canManageConnection(item.connection)"
                class="host-action-btn host-action-btn--mute"
                type="button"
                title="Mute participant"
                @click.stop="forceMuteParticipant(getUserIdOfConnection(item.connection) ?? '')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="host-btn-icon">
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </svg>
              </button>

              <button
                v-if="canManageConnection(item.connection)"
                class="host-action-btn host-action-btn--kick"
                type="button"
                title="Kick participant"
                @click.stop="requestKickParticipant(item.connection)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="host-btn-icon">
                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
                  <line x1="12" y1="2" x2="12" y2="12"></line>
                </svg>
              </button>

              <!-- Fullscreen action overlay -->
              <button
                class="fullscreen-action-btn"
                type="button"
                :title="fullscreenStreamId === item.streamManager?.stream?.streamId ? 'Exit Fullscreen' : 'Fullscreen'"
                @click.stop="handleFullscreenClick"
              >
                <svg v-if="fullscreenStreamId === item.streamManager?.stream?.streamId" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="fullscreen-btn-icon">
                  <polyline points="4 14 10 14 10 20"></polyline>
                  <polyline points="20 10 14 10 14 4"></polyline>
                  <line x1="10" y1="14" x2="3" y2="21"></line>
                  <line x1="21" y1="3" x2="14" y2="10"></line>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="fullscreen-btn-icon">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div id="video-sources-container" class="col-md-6" v-show="sidebarStreams.length > 0">
          <div ref="container" class="others-video-container-wrapper">
            <div class="others-video-container">
              <div
                v-for="item in sidebarStreams"
                :key="item.id"
                style="position:relative;display:inline-block;vertical-align:top;"
                class="sidebar-video-wrapper"
                :class="{ 'is-screen-share': item.isScreen }"
              >
                <user-video
                  class="others-video"
                  :class="{
                    'speaking-indicator': speakingMap[item.connection?.connectionId ?? '']
                  }"
                  :stream-manager="item.streamManager"
                  :connection="item.connection"
                />
                <MicOn
                  v-if="speakingMap[item.connection?.connectionId ?? '']"
                  class="mute-overlay mute-overlay--small speaking-mic-overlay"
                />
                <MicOff
                  v-else-if="item.streamManager && subscriberMutedMap[item.streamManager.stream?.streamId ?? '']"
                  class="mute-overlay mute-overlay--small"
                />
                <MicOff
                  v-else-if="item.streamManager === publisher && isMicMuted"
                  class="mute-overlay mute-overlay--small"
                />
                <CamOff
                  v-if="item.streamManager && subscriberVideoOffMap[item.streamManager.stream?.streamId ?? '']"
                  class="cam-overlay cam-overlay--small"
                />
                <CamOff
                  v-else-if="item.streamManager === publisher && isVideoOff"
                  class="cam-overlay cam-overlay--small"
                />

                 <!-- Host action overlays (Mute/Kick) in sidebar -->
                <button
                  v-if="canManageConnection(item.connection)"
                  class="host-action-btn host-action-btn--sidebar host-action-btn--mute"
                  type="button"
                  title="Mute participant"
                  @click.stop="forceMuteParticipant(getUserIdOfConnection(item.connection) ?? '')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="host-btn-icon">
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                    <line x1="12" y1="19" x2="12" y2="23"></line>
                    <line x1="8" y1="23" x2="16" y2="23"></line>
                  </svg>
                </button>

                <button
                  v-if="canManageConnection(item.connection)"
                  class="host-action-btn host-action-btn--sidebar host-action-btn--kick"
                  type="button"
                  title="Kick participant"
                  @click.stop="requestKickParticipant(item.connection)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="host-btn-icon">
                    <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
                    <line x1="12" y1="2" x2="12" y2="12"></line>
                  </svg>
                </button>

                <!-- Pin action overlay in sidebar -->
                <button
                  class="pin-action-btn pin-action-btn--sidebar"
                  type="button"
                  title="Pin"
                  @click.stop="togglePin(item.id)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="pin-btn-icon">
                    <line x1="12" y1="17" x2="12" y2="22"></line>
                    <path d="M5 17h14v-1.76a2 2 0 0 0-.44-1.24l-2.78-3.5A2 2 0 0 1 15 9.24V5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v4.24c0 .43-.14.85-.4 1.21l-2.78 3.51A2 2 0 0 0 5 15.24Z"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Mute Speaking Warning Banner -->
      <Transition name="slide-up">
        <div
          v-if="isMicMuted && showMuteWarning"
          class="mute-speaking-banner"
          @click="toggleMic"
          title="Apasă pentru a activa microfonul"
        >
          <div class="mute-speaking-content">
            <MicOff class="banner-mic-icon" />
            <div class="banner-text">
              <span class="banner-title">Ești pe mut!</span>
              <span class="banner-subtitle">Apasă aici sau pe microfon pentru a vorbi</span>
            </div>
          </div>
        </div>
      </Transition>

      <div id="session-controls">
        <div class="session-controls-left">
          <span class="app-title-text">{{ appTitle }}</span>
        </div>
        <div class="session-controls-center">
          <div class="session-control-group">
            <button
              class="preview-toggle session-control-group__main"
              type="button"
              :aria-label="micToggleLabel"
              :title="micToggleLabel"
              @click="toggleMic"
            >
              <MicOff v-if="isMicMuted" class="toggle-icon" />
              <MicOn v-else class="toggle-icon" />
            </button>
            <div class="session-control-dropdown-wrap">
              <button
                class="preview-toggle session-control-dropdown-trigger session-control-group__trigger"
                type="button"
                aria-label="Select audio devices"
                title="Select audio devices"
                aria-haspopup="listbox"
                :aria-expanded="showMicDropdown"
                @click.stop="showMicDropdown = !showMicDropdown; showCameraDropdown = false"
              >
                <span class="dropdown-arrow">▼</span>
              </button>
              <div
                v-if="showMicDropdown"
                class="session-control-dropdown unified-audio-dropdown"
              >
                <!-- Microphone Section -->
                <div class="dropdown-section-header">{{ t.microphoneLabel }}</div>
                <ul role="listbox" aria-label="Microphone list">
                  <li
                    v-for="mic in microphones"
                    :key="mic.deviceId"
                    role="option"
                    :aria-selected="selectedMicId === mic.deviceId"
                    class="session-control-dropdown-item device-item"
                    :class="{ 'device-item--selected': selectedMicId === mic.deviceId }"
                    @click="switchMicrophone(mic.deviceId)"
                  >
                    <span class="checkmark" v-if="selectedMicId === mic.deviceId">✓</span>
                    <span class="checkmark-placeholder" v-else></span>
                    <span class="device-name">{{ formatMicLabel(mic) }}</span>
                  </li>
                  <li v-if="!microphones.length" class="session-control-dropdown-item session-control-dropdown-item--empty">
                    No microphones found
                  </li>
                </ul>

                <div class="dropdown-divider"></div>

                <!-- Speaker Section -->
                <div class="dropdown-section-header">{{ t.speakerLabel }}</div>
                <ul role="listbox" aria-label="Speaker list">
                  <li
                    v-for="spk in speakers"
                    :key="spk.deviceId"
                    role="option"
                    :aria-selected="selectedSpeakerId === spk.deviceId"
                    class="session-control-dropdown-item device-item"
                    :class="{ 'device-item--selected': selectedSpeakerId === spk.deviceId }"
                    @click="switchSpeaker(spk.deviceId)"
                  >
                    <span class="checkmark" v-if="selectedSpeakerId === spk.deviceId">✓</span>
                    <span class="checkmark-placeholder" v-else></span>
                    <span class="device-name">{{ formatSpeakerLabel(spk) }}</span>
                  </li>
                  <li v-if="!speakers.length" class="session-control-dropdown-item session-control-dropdown-item--empty">
                    No speakers found
                  </li>
                </ul>

                <div class="dropdown-divider"></div>

                <!-- Test Speaker Section -->
                <button
                  class="session-control-dropdown-item test-speaker-btn"
                  type="button"
                  @click="testSpeaker"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="test-speaker-icon">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                  </svg>
                  <span>{{ t.testSpeakerLabel }}</span>
                </button>
              </div>
            </div>
          </div>
          <div class="session-control-group">
            <button
              class="preview-toggle session-control-group__main"
              type="button"
              :aria-label="videoToggleLabel"
              :title="videoToggleLabel"
              @click="toggleVideo"
            >
              <CamOff v-if="isVideoOff" class="toggle-icon" />
              <CamOn v-else class="toggle-icon" />
            </button>
            <div class="session-control-dropdown-wrap">
              <button
                class="preview-toggle session-control-dropdown-trigger session-control-group__trigger"
                type="button"
                aria-label="Select camera"
                title="Select camera"
                aria-haspopup="listbox"
                :aria-expanded="showCameraDropdown"
                @click.stop="showCameraDropdown = !showCameraDropdown; showMicDropdown = false; showSpeakerDropdown = false"
              >
                <span class="dropdown-arrow">▼</span>
              </button>
              <ul
                v-if="showCameraDropdown"
                class="session-control-dropdown"
                role="listbox"
                aria-label="Camera list"
              >
                <li
                  v-for="cam in cameras"
                  :key="cam.deviceId"
                  role="option"
                  :aria-selected="selectedCameraId === cam.deviceId"
                  class="session-control-dropdown-item"
                  @click="switchCamera(cam.deviceId)"
                >
                  {{ formatCameraLabel(cam) }}
                </li>
                <li v-if="!cameras.length" class="session-control-dropdown-item session-control-dropdown-item--empty">
                  No cameras found
                </li>
              </ul>
            </div>
          </div>
          <button
            v-if="!isMobileDevice"
            class="preview-toggle"
            :class="{ 'screen-share-active': isScreenSharing }"
            type="button"
            :aria-label="isScreenSharing ? t.stopShareScreen : t.shareScreen"
            :title="isScreenSharing ? t.stopShareScreen : t.shareScreen"
            @click="toggleScreenShare"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <path d="M8 21h8M12 17v4"/>
              <polyline points="15 9.5 12 6.5 9 9.5"/>
              <line x1="12" y1="6.5" x2="12" y2="13.5"/>
            </svg>
          </button>
          <button
            class="preview-toggle invite-toggle-btn"
            :class="{ 'invite-toggle-btn--active': showInviteModal, 'invite-toggle-btn--copied': isInviteLinkCopied }"
            type="button"
            aria-label="Invită colegi în apel"
            title="Invită colegi în apel"
            @click="toggleInviteModal"
          >
            <template v-if="!isInviteLinkCopied">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
            </template>
            <template v-else>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span class="invite-copied-text">Link copiat!</span>
            </template>
          </button>
          <button
            class="preview-toggle chat-toggle-btn"
            :class="{ 'chat-toggle-btn--active': isChatOpen }"
            type="button"
            aria-label="Chat"
            title="Chat convorbire"
            @click="toggleChatPanel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
            <span v-if="unreadChatCount > 0" class="chat-badge-counter">{{ unreadChatCount }}</span>
          </button>
          <button
            class="preview-toggle minimize-btn"
            type="button"
            :aria-label="minimized ? 'Maximize' : 'Minimize'"
            :title="minimized ? 'Maximize' : 'Minimize'"
            @click="toggleMinimize"
          >
            <svg v-if="!minimized" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
              <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
              <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
            </svg>
          </button>
          <button
            class="preview-toggle"
            type="button"
            :aria-label="layoutPosition === 'right' ? 'Move participants top' : 'Move participants right'"
            :title="layoutPosition === 'right' ? 'Move participants top' : 'Move participants right'"
            @click="toggleLayout"
          >
            <svg v-if="layoutPosition === 'right'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="15" y1="3" x2="15" y2="21"/>
              <line x1="15" y1="12" x2="21" y2="12"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="toggle-icon">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="12" y1="3" x2="12" y2="9"/>
            </svg>
          </button>
          <button
            class="preview-toggle exit-button"
            type="button"
            :aria-label="t.leaveBtn"
            :title="t.leaveBtn"
            @click="clickExitButton"
          >
            <ExitDoor class="toggle-icon" />
            <span class="exit-btn-text">{{ t.leaveBtn }}</span>
          </button>
        </div>
        <div class="session-controls-right">
          <span class="timer-dot" :class="{ 'timer-dot--active': hasMeetingStarted }"></span>
          <span class="timer-text">{{ formattedMeetingTime }}</span>
        </div>
      </div>
    </div>

    <!-- Inline Session Chat Drawer -->
    <div
      v-if="isChatOpen && !minimized"
      class="session-chat-drawer"
      :class="{ 'is-dragging-over': isDraggingOver }"
      @paste="handlePaste"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <!-- Drag & Drop Visual Overlay -->
      <div v-if="isDraggingOver" class="chat-drag-overlay">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="drag-icon">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="17 8 12 3 7 8"></polyline>
          <line x1="12" y1="3" x2="12" y2="15"></line>
        </svg>
        <span>Eliberează fișierul aici pentru a-l atașa în chat</span>
      </div>
      <div class="chat-drawer-header">
        <div class="chat-header-title">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chat-title-icon">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span>Chat</span>
        </div>
        <div class="chat-header-actions">
          <button
            v-if="chatMessages.length > 0"
            type="button"
            class="chat-drawer-action-btn"
            title="Descarcă Istoric Chat (.md)"
            @click="exportChatTranscript"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-icon">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </button>
          <button type="button" class="chat-drawer-close" @click="toggleChatPanel">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="close-icon">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div class="chat-drawer-feed" ref="chatFeedContainer">
        <div v-if="chatMessages.length === 0" class="chat-feed-empty">
          Nu există mesaje în această întâlnire.
        </div>
        <div
          v-for="(msg, index) in chatMessages"
          :key="index"
          class="chat-message-bubble-wrapper"
          :class="{ 'chat-message-bubble-wrapper--own': isOwnMessage(msg) }"
        >
          <div class="chat-message-content-group">
            <div
              class="chat-avatar-circle"
              :style="{ backgroundColor: getAvatarColor(msg.senderName) }"
              :title="msg.senderName"
            >
              {{ getInitials(msg.senderName) }}
            </div>
            <div class="chat-message-body-area">
              <div class="chat-message-info">
                <span class="chat-sender-name">{{ msg.senderName }}</span>
                <span class="chat-timestamp">{{ formatChatTime(msg.timestamp) }}</span>
              </div>
              <div class="chat-message-text">
                <div v-if="msg.replyTo" class="chat-quote-box">
                  <span class="quote-sender">{{ msg.replyTo.senderName }}</span>
                  <span class="quote-text">{{ msg.replyTo.text }}</span>
                </div>
                <div v-if="msg.attachment" class="chat-attachment-wrapper">
                  <div v-if="msg.attachment.type.startsWith('image/')" class="chat-attachment-img-link" @click="openImagePreview(msg.attachment)">
                    <img :src="msg.attachment.url" :alt="msg.attachment.name" class="chat-attachment-img" />
                  </div>
                  <div v-else class="chat-attachment-file-card">
                    <div class="file-card-main" @click="openAttachment(msg.attachment.url, msg.attachment.name)">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="file-card-icon">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      <div class="file-card-info">
                        <span class="file-card-name">{{ msg.attachment.name }}</span>
                        <span class="file-card-size">{{ formatFileSize(msg.attachment.size) }}</span>
                      </div>
                    </div>
                    <a :href="msg.attachment.url" :download="msg.attachment.name" class="file-download-link" title="Descarcă fișierul">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="file-download-icon">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </a>
                  </div>
                </div>
                <div v-if="msg.text" v-html="formatChatMessageHtml(msg.text)"></div>
              </div>
            </div>
            <div class="chat-bubble-actions">
              <button
                type="button"
                class="chat-action-bubble-btn"
                title="Răspunde"
                @click="setReplyToMessage(msg)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-bubble-icon">
                  <polyline points="9 17 4 12 9 7"></polyline>
                  <path d="M20 18v-2a4 4 0 0 0-4-4H4"></path>
                </svg>
              </button>
              <button
                type="button"
                class="chat-action-bubble-btn"
                :title="copiedMessageIndex === index ? 'Copiat!' : 'Copiază mesajul'"
                @click="copyMessageText(msg.text, index)"
              >
                <svg v-if="copiedMessageIndex !== index" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-bubble-icon">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <span v-else class="copied-check">✓</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-drawer-footer">
        <input
          ref="fileInputRef"
          type="file"
          style="display: none;"
          accept="image/*,.pdf,.doc,.docx,.txt"
          @change="handleFileSelect"
        />

        <div v-if="attachmentError" class="chat-error-banner">
          {{ attachmentError }}
        </div>

        <div v-if="pendingAttachment" class="chat-attachment-preview-bar">
          <div class="attachment-preview-info">
            <img v-if="pendingAttachment.type.startsWith('image/')" :src="pendingAttachment.url" class="preview-thumb-img" alt="Preview" />
            <span v-else class="attachment-icon">📎</span>
            <span class="attachment-name">{{ pendingAttachment.name }}</span>
            <span class="attachment-size">({{ formatFileSize(pendingAttachment.size) }})</span>
          </div>
          <button type="button" class="attachment-remove-btn" title="Șterge atașament" @click="removePendingAttachment">
            ✕
          </button>
        </div>

        <div v-if="replyingTo" class="chat-reply-preview-bar">
          <div class="reply-preview-content">
            <span class="reply-sender">Răspunde lui <strong>{{ replyingTo.senderName }}</strong>:</span>
            <span class="reply-text">"{{ replyingTo.text }}"</span>
          </div>
          <button type="button" class="reply-cancel-btn" title="Anulează citat" @click="cancelReply">
            ✕
          </button>
        </div>
        <div class="chat-formatting-toolbar">
          <button type="button" class="chat-format-btn" title="Text Îngroșat (Bold)" @click="insertFormatting('bold')">
            <strong>B</strong>
          </button>
          <button type="button" class="chat-format-btn" title="Text Înclinat (Italic)" @click="insertFormatting('italic')">
            <em>I</em>
          </button>
          <button type="button" class="chat-format-btn" title="Adaugă Link" @click="insertFormatting('link')">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="link-icon">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
          </button>
          <button type="button" class="chat-format-btn" title="Atașează fișier (max 5 MB)" @click="triggerFilePicker">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="paperclip-icon">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
            </svg>
          </button>
        </div>

        <div class="chat-input-row">
          <input
            ref="chatInputRef"
            v-model="chatInputText"
            type="text"
            class="chat-input-field"
            placeholder="Scrie un mesaj sau lipește o imagine (Ctrl+V)..."
            @keyup.enter="sendChatMessage"
            @paste="handlePaste"
          />
          <button type="button" class="chat-send-btn" @click="sendChatMessage">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="send-icon">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Search and Invite Colleague Modal -->
    <div v-if="showInviteModal" class="invite-modal-overlay" @click.self="showInviteModal = false">
      <div class="invite-modal" role="dialog" aria-modal="true" aria-labelledby="invite-modal-title">
        <div class="invite-modal-header">
          <div class="invite-modal-title-wrapper">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-header-icon">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            <h3 id="invite-modal-title" class="invite-modal-title">Invită persoane în apel</h3>
          </div>
          <button type="button" class="invite-modal-close" aria-label="Închide" @click="showInviteModal = false">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-btn-icon">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="invite-copy-section">
          <div class="invite-copy-info">
            <span class="invite-copy-label">Link întâlnire</span>
            <span class="invite-copy-sub">Trimite link-ul oricărei persoane pentru a se alătura</span>
          </div>
          <button
            class="invite-copy-btn"
            :class="{ 'invite-copy-btn--copied': isInviteLinkCopied }"
            type="button"
            @click="copyInviteLink"
          >
            <template v-if="!isInviteLinkCopied">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-btn-icon">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              <span>Copiază link</span>
            </template>
            <template v-else>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="invite-btn-icon">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <span>Link copiat!</span>
            </template>
          </button>
        </div>

        <div class="invite-divider">
          <span>sau caută un coleg</span>
        </div>

        <div class="invite-search-box">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-search-icon">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model="colleagueSearchQuery"
            type="text"
            class="invite-search-input"
            placeholder="Caută colegi după nume sau rol..."
          />
          <button
            v-if="colleagueSearchQuery"
            type="button"
            class="invite-search-clear"
            @click="colleagueSearchQuery = ''"
          >
            ✕
          </button>
        </div>

        <div class="invite-colleague-list">
          <div v-if="loadingColleagues" class="invite-state-msg">
            <div class="invite-spinner"></div>
            <span>Se încarcă colegii...</span>
          </div>

          <div v-else-if="errorColleagues" class="invite-state-msg invite-error">
            <span>Nu s-au putut încărca colegii.</span>
          </div>

          <div v-else-if="filteredColleagues.length === 0" class="invite-state-msg">
            <span v-if="colleagueSearchQuery">Niciun coleg găsit pentru „{{ colleagueSearchQuery }}”.</span>
            <span v-else>Nu există colegi în listă.</span>
          </div>

          <div
            v-for="colleague in filteredColleagues"
            :key="colleague.id"
            class="invite-colleague-item"
          >
            <div class="invite-colleague-left">
              <div class="invite-avatar-wrapper">
                <div class="invite-avatar">
                  {{ getInitials(colleague.name) }}
                </div>
                <span
                  class="invite-status-dot"
                  :class="parsedOnlineUsers.includes(colleague.id) ? 'invite-status-dot--online' : 'invite-status-dot--offline'"
                ></span>
              </div>

              <div class="invite-colleague-info">
                <div class="invite-colleague-name">{{ colleague.name }}</div>
                <div class="invite-colleague-meta">
                  <span class="invite-colleague-role">{{ formatRoleName(colleague.roles) }}</span>
                  <span v-if="parsedInCallUsers.includes(colleague.id)" class="invite-badge invite-badge--incall">În apel</span>
                  <span v-else-if="parsedInPreviewUsers.includes(colleague.id)" class="invite-badge invite-badge--waiting">În așteptare</span>
                  <span v-else-if="parsedOnlineUsers.includes(colleague.id)" class="invite-badge invite-badge--online">Online</span>
                  <span v-else class="invite-badge invite-badge--offline">Offline</span>
                </div>
              </div>
            </div>

            <div class="invite-colleague-right">
              <button
                v-if="parsedOnlineUsers.includes(colleague.id)"
                class="invite-call-btn"
                :class="{ 'invite-call-btn--sent': invitedUserMap[colleague.id] }"
                :disabled="parsedInCallUsers.includes(colleague.id) || parsedInPreviewUsers.includes(colleague.id)"
                type="button"
                @click="inviteColleague(colleague.id)"
              >
                <template v-if="invitedUserMap[colleague.id]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="invite-btn-icon">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>Invitat!</span>
                </template>
                <template v-else>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-btn-icon">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <span>Invită</span>
                </template>
              </button>

              <button
                v-else
                class="invite-copy-user-btn"
                type="button"
                title="Persoana este offline. Copiază link-ul pentru a i-l trimite."
                @click="copyInviteLink"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="invite-btn-icon">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                <span>Link</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Lightbox Modal -->
    <div v-if="activeImagePreviewUrl" class="chat-lightbox-overlay" @click="closeImagePreview">
      <div class="chat-lightbox-content" @click.stop>
        <div class="chat-lightbox-header">
          <span class="chat-lightbox-filename">{{ activeImagePreviewName }}</span>
          <div class="chat-lightbox-actions">
            <a :href="activeImagePreviewUrl" :download="activeImagePreviewName || 'image.png'" class="chat-lightbox-btn" title="Descarcă imaginea">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-icon">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
            </a>
            <button type="button" class="chat-lightbox-btn" title="Închide" @click="closeImagePreview">✕</button>
          </div>
        </div>
        <div class="chat-lightbox-body">
          <img :src="activeImagePreviewUrl" class="chat-lightbox-img" />
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="minimized"
    class="pip-maximize-overlay"
    @mousedown.stop.prevent="handleDragStart"
    @touchstart.stop="handleDragStart"
    title="Drag to move / Click to maximize"
  />

  <div v-if="minimized" class="minimized-controls-bar">
    <button
      class="minimized-control-btn"
      :class="{ 'minimized-control-btn--active': isMicMuted }"
      type="button"
      :aria-label="micToggleLabel"
      :title="micToggleLabel"
      @mousedown.stop
      @touchstart.stop
      @click.stop="toggleMic"
    >
      <MicOff v-if="isMicMuted" class="control-icon" />
      <MicOn v-else class="control-icon" />
    </button>
    <button
      class="minimized-control-btn"
      :class="{ 'minimized-control-btn--active': isVideoOff }"
      type="button"
      :aria-label="videoToggleLabel"
      :title="videoToggleLabel"
      @mousedown.stop
      @touchstart.stop
      @click.stop="toggleVideo"
    >
      <CamOff v-if="isVideoOff" class="control-icon" />
      <CamOn v-else class="control-icon" />
    </button>
  </div>

  <!-- Confirm Exit Modal -->
  <div v-if="showExitConfirm" class="exit-confirm-overlay" @click.self="showExitConfirm = false">
    <div class="exit-confirm-dialog">
      <div class="exit-confirm-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="warning-icon">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <h3 class="exit-confirm-title">{{ t.confirmTitle }}</h3>
      <p class="exit-confirm-message">{{ t.confirmMessage }}</p>
      <div class="exit-confirm-actions">
        <button class="exit-confirm-btn exit-confirm-btn--cancel" @click="showExitConfirm = false">
          {{ t.confirmCancel }}
        </button>
        <button class="exit-confirm-btn exit-confirm-btn--confirm" @click="confirmExit">
          {{ t.confirmExit }}
        </button>
      </div>
    </div>
  </div>

  <!-- Confirm Kick Modal -->
  <div v-if="showKickConfirm" class="exit-confirm-overlay" @click.self="showKickConfirm = false">
    <div class="exit-confirm-dialog">
      <div class="exit-confirm-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="warning-icon">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <h3 class="exit-confirm-title">{{ t.kickTitle }}</h3>
      <p class="exit-confirm-message">{{ t.kickMessage.replace('{name}', kickTargetName) }}</p>
      <div class="exit-confirm-actions">
        <button class="exit-confirm-btn exit-confirm-btn--cancel" @click="showKickConfirm = false">
          {{ t.kickCancel }}
        </button>
        <button class="exit-confirm-btn exit-confirm-btn--confirm" @click="confirmKickParticipant">
          {{ t.kickConfirm }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss"></style>
